export type Language = 'pt' | 'en' | 'es' | 'nl' | 'de' | 'fr' | 'it' | 'no' | 'da' | 'sv';

export interface LanguageOption {
  code: Language;
  name: string;
  flag: string;
}

export const languages: LanguageOption[] = [
  { code: 'pt', name: 'Português', flag: '🇵🇹' },
  { code: 'en', name: 'English', flag: '🇬🇧' },
  { code: 'es', name: 'Español', flag: '🇪🇸' },
  { code: 'nl', name: 'Nederlands', flag: '🇳🇱' },
  { code: 'de', name: 'Deutsch', flag: '🇩🇪' },
  { code: 'fr', name: 'Français', flag: '🇫🇷' },
  { code: 'it', name: 'Italiano', flag: '🇮🇹' },
  { code: 'no', name: 'Norsk', flag: '🇳🇴' },
  { code: 'da', name: 'Dansk', flag: '🇩🇰' },
  { code: 'sv', name: 'Svenska', flag: '🇸🇪' },
];

type TranslationKeys = {
  // Nav
  nav_home: string;
  nav_about: string;
  nav_services: string;
  nav_contact: string;
  nav_facilities: string;
  nav_academy: string;

  // Facilities
  facilities_title: string;
  facilities_subtitle: string;
  facilities_video_title: string;

  // Hero
  hero_title: string;
  hero_subtitle: string;
  hero_cta_booking: string;
  hero_cta_academy: string;

  // About
  about_title: string;
  about_subtitle: string;
  about_description: string;
  about_academy_title: string;
  about_academy_desc: string;
  about_academy_button: string;
  about_stat_pain: string;
  about_stat_pain_label: string;
  about_stat_discs: string;
  about_stat_discs_label: string;
  about_stat_countries: string;
  about_stat_countries_label: string;
  about_stat_years: string;
  about_stat_years_label: string;
  about_device_title: string;
  about_device_desc: string;
  about_device_button: string;
  popup_back: string;

  // Services
  services_title: string;
  services_subtitle: string;
  service_pain: string;
  service_pain_desc: string;
  service_neuro: string;
  service_neuro_desc: string;
  service_inflammation: string;
  service_inflammation_desc: string;
  service_recovery: string;
  service_recovery_desc: string;
  services_how_title: string;
  services_step1: string;
  services_step1_desc: string;
  services_step2: string;
  services_step2_desc: string;
  services_step3: string;
  services_step3_desc: string;

  // Contact
  contact_title: string;
  contact_subtitle: string;
  contact_name: string;
  contact_email: string;
  contact_phone: string;
  contact_message: string;
  contact_send: string;
  contact_address: string;
  contact_schedule: string;
  contact_schedule_value: string;

  // Footer
  footer_rights: string;
  footer_privacy: string;
  footer_terms: string;
  footer_tagline: string;

  // Privacy Policy
  privacy_last_updated: string;
  privacy_section1_title: string;
  privacy_section1_text: string;
  privacy_section2_title: string;
  privacy_section2_text: string;
  privacy_section3_title: string;
  privacy_section3_text: string;
  privacy_section4_title: string;
  privacy_section4_text: string;
  privacy_section5_title: string;
  privacy_section5_text: string;
  privacy_section6_title: string;
  privacy_section6_text: string;

  // Terms
  terms_last_updated: string;
  terms_section1_title: string;
  terms_section1_text: string;
  terms_section2_title: string;
  terms_section2_text: string;
  terms_section3_title: string;
  terms_section3_text: string;
  terms_section4_title: string;
  terms_section4_text: string;
  terms_section5_title: string;
  terms_section5_text: string;
  terms_section6_title: string;
  terms_section6_text: string;

  // Academy
  academy_hero_title: string;
  academy_hero_subtitle: string;
  academy_pillars_title: string;
  academy_pillar1_title: string;
  academy_pillar1_desc: string;
  academy_pillar2_title: string;
  academy_pillar2_desc: string;
  academy_pillar3_title: string;
  academy_pillar3_desc: string;
  academy_pillar4_title: string;
  academy_pillar4_desc: string;
  academy_curriculum: string;
  academy_edu_title: string;
  academy_edu_subtitle: string;
  academy_level1_title: string;
  academy_level1_topics: string;
  academy_level2_title: string;
  academy_level2_topics: string;
  academy_level3_title: string;
  academy_level3_topics: string;
  academy_level4_title: string;
  academy_level4_topics: string;
  academy_level5_title: string;
  academy_level5_topics: string;
  academy_level6_title: string;
  academy_level6_topics: string;
  academy_impl_title: string;
  academy_impl_subtitle: string;
  academy_impl_tools: string;
  academy_join_title: string;
  academy_join_subtitle: string;
  academy_join_roles: string;

  // Cookie Consent
  cookie_text: string;
  cookie_accept: string;
  cookie_reject: string;
  cookie_privacy_link: string;
};

export const translations: Record<Language, TranslationKeys> = {
  pt: {
    nav_home: 'Início',
    nav_about: 'Sobre Nós',
    nav_services: 'Serviços',
    nav_contact: 'Contacto',
    nav_facilities: 'Instalações',
    nav_academy: 'Academia',
    facilities_title: 'As Nossas Instalações',
    facilities_subtitle: 'Conheça o nosso espaço privilegiado em Porches, Algarve — um ambiente de excelência para o bem-estar animal.',
    facilities_video_title: 'Vídeo da Clínica',
    hero_title: 'Clínica & Academia de Terapia de Frequência Animal',
    hero_subtitle: 'Tecnologia ANF revolucionária para o bem-estar do seu animal. Tratamentos não invasivos com discos de frequência terapêutica.',
    hero_cta_booking: 'Marcar Consulta',
    hero_cta_academy: 'Formação Profissional',
    about_title: 'Sobre a ANFAnimal Portugal',
    about_subtitle: 'Excelência em terapia animal com tecnologia de ponta',
    about_description: 'A ANFAnimal Portugal é a clínica de referência em terapia de frequência para animais na Península Ibérica. Combinamos a tecnologia patenteada ANF com uma equipa de profissionais altamente qualificados para oferecer os melhores tratamentos ao seu animal.',
    about_academy_title: 'Academia de Formação',
    about_academy_desc: 'Formamos profissionais veterinários na tecnologia ANF Animal. Cursos certificados com prática clínica supervisionada.',
    about_academy_button: 'Saber Mais',
    about_stat_pain: '+50%',
    about_stat_pain_label: 'Redução da Dor',
    about_stat_discs: '30+',
    about_stat_discs_label: 'Discos Terapêuticos',
    about_stat_countries: '40+',
    about_stat_countries_label: 'Países',
    about_stat_years: '10+',
    about_stat_years_label: 'Anos de Investigação',
    about_device_title: 'Dispositivo Médico ANF',
    about_device_desc: 'Conheça a tecnologia patenteada ANF Animal — discos de frequência terapêutica que revolucionam o tratamento animal.',
    about_device_button: 'Ver Dispositivo Médico',
    popup_back: 'Voltar à Web',
    services_title: 'Serviços & Tratamentos',
    services_subtitle: 'Terapias de frequência não invasivas para o seu animal',
    service_pain: 'Gestão da Dor',
    service_pain_desc: 'Tratamento eficaz da dor crónica e aguda através de discos de frequência ANF. Resultados visíveis desde a primeira sessão.',
    service_neuro: 'Terapia Neurológica',
    service_neuro_desc: 'Frequências específicas para condições neurológicas, melhorando a comunicação nervosa e a mobilidade.',
    service_inflammation: 'Anti-Inflamação',
    service_inflammation_desc: 'Redução natural da inflamação sem medicamentos, utilizando frequências terapêuticas específicas.',
    service_recovery: 'Recuperação Pós-Cirúrgica',
    service_recovery_desc: 'Aceleração do processo de recuperação após cirurgias, com discos que promovem a regeneração celular.',
    services_how_title: 'Como Funciona',
    services_step1: 'Avaliação',
    services_step1_desc: 'Exame completo do animal para identificar áreas de tratamento.',
    services_step2: 'Aplicação',
    services_step2_desc: 'Colocação estratégica dos discos de frequência ANF.',
    services_step3: 'Resultados',
    services_step3_desc: 'Melhoria progressiva com sessões de acompanhamento.',
    contact_title: 'Contacte-nos',
    contact_subtitle: 'Marque a sua consulta ou peça mais informações',
    contact_name: 'Nome',
    contact_email: 'Email',
    contact_phone: 'Telefone',
    contact_message: 'Mensagem',
    contact_send: 'Enviar Mensagem',
    contact_address: 'Porches, Algarve',
    contact_schedule: 'Horário',
    contact_schedule_value: 'Seg-Sex: 9h-18h | Sáb: 9h-13h',
    footer_rights: 'Todos os direitos reservados.',
    footer_privacy: 'Política de Privacidade',
    footer_terms: 'Termos e Condições',
    footer_tagline: 'Terapia de Frequência Animal Premium',
    privacy_last_updated: 'Última atualização: Março 2026',
    privacy_section1_title: 'Informação que Recolhemos',
    privacy_section1_text: 'Recolhemos informações pessoais que nos fornece voluntariamente ao contactar-nos, incluindo nome, email, telefone e detalhes sobre o seu animal de estimação.',
    privacy_section2_title: 'Utilização da Informação',
    privacy_section2_text: 'Utilizamos as suas informações para prestar os nossos serviços de terapia ANF Animal, agendar consultas, comunicar consigo e melhorar os nossos serviços.',
    privacy_section3_title: 'Proteção de Dados',
    privacy_section3_text: 'Implementamos medidas de segurança adequadas para proteger as suas informações pessoais contra acesso não autorizado, alteração ou destruição.',
    privacy_section4_title: 'Cookies',
    privacy_section4_text: 'O nosso website utiliza cookies para melhorar a sua experiência de navegação. Pode configurar o seu navegador para recusar cookies.',
    privacy_section5_title: 'Os Seus Direitos',
    privacy_section5_text: 'Ao abrigo do RGPD, tem direito a aceder, retificar, apagar e portar os seus dados pessoais. Contacte-nos para exercer estes direitos.',
    privacy_section6_title: 'Contacto',
    privacy_section6_text: 'Para questões sobre privacidade, contacte-nos em geral@anfanimalclinicportugal.com, pelo telefone +351 928 281 833, ou visite a nossa clínica em R. Porches Velho, 8400-490 Porches.',
    terms_last_updated: 'Última atualização: Março 2026',
    terms_section1_title: 'Aceitação dos Termos',
    terms_section1_text: 'Ao aceder e utilizar o website da ANFAnimal Portugal, concorda com estes termos e condições. Se não concordar, não utilize o website.',
    terms_section2_title: 'Serviços',
    terms_section2_text: 'A ANFAnimal Portugal oferece terapia de frequência não invasiva para animais através da tecnologia ANF. Os resultados podem variar e não garantimos resultados específicos.',
    terms_section3_title: 'Propriedade Intelectual',
    terms_section3_text: 'Todo o conteúdo do website, incluindo textos, imagens, logótipos e design, é propriedade da ANFAnimal Portugal e está protegido por direitos de autor.',
    terms_section4_title: 'Limitação de Responsabilidade',
    terms_section4_text: 'A ANFAnimal Portugal não se responsabiliza por danos indiretos resultantes da utilização dos nossos serviços. A terapia ANF é complementar e não substitui tratamento veterinário convencional.',
    terms_section5_title: 'Cancelamentos e Reembolsos',
    terms_section5_text: 'As consultas podem ser canceladas com 24 horas de antecedência sem custos. Cancelamentos tardios podem estar sujeitos a taxa.',
    terms_section6_title: 'Lei Aplicável',
    terms_section6_text: 'Estes termos regem-se pela legislação portuguesa. Qualquer litígio será submetido aos tribunais de Portimão, Portugal.',
    academy_hero_title: 'Academia de Formação',
    academy_hero_subtitle: 'Encontre a causa raiz de qualquer dor, lesão e/ou patologia e compreenda a anatomia animal como uma unidade. 6 níveis educativos para dominar a ANF Animal Therapy®.',
    academy_pillars_title: 'Pilares Fundamentais',
    academy_pillar1_title: 'Função Neurológica',
    academy_pillar1_desc: 'A complexidade e funções do sistema nervoso que permite ao animal a autocura com o uso da ANF Animal Therapy®.',
    academy_pillar2_title: 'Bioquímica e Recursos',
    academy_pillar2_desc: 'O papel das células, moléculas, hormonas e vitaminas e as suas interações necessárias para facilitar as fusões e frequências requeridas para reparar as disfunções.',
    academy_pillar3_title: 'Medicina de Neurofrequência',
    academy_pillar3_desc: 'Normalizar a inflamação e a disfunção neurológica com o dispositivo tecnológico mais eficaz, utilizando tecnologia revolucionária e avançada com neurofrequências que permite ao corpo auto-reparar-se sem efeitos secundários ou medicamentos.',
    academy_pillar4_title: 'Formação Clínica e Implementação',
    academy_pillar4_desc: 'Aprenda técnicas de exame neurológico para obter competências palpatórias e práticas excecionais nos sistemas musculoesquelético, nervoso, linfático, digestivo e endócrino.',
    academy_curriculum: 'Currículo',
    academy_edu_title: '6 Níveis Educativos',
    academy_edu_subtitle: 'Um percurso estruturado para dominar a ANF Animal Therapy®',
    academy_level1_title: 'Dor e Inflamação',
    academy_level1_topics: 'Frequências e Metodologia,Dor e Inflamação,Antioxidantes,Protocolos e Aplicação',
    academy_level2_title: 'Sistema Nervoso',
    academy_level2_topics: 'Sistema Nervoso,Comparação de Espécies,Planos de Tratamento,Protocolos e Aplicação',
    academy_level3_title: 'Sistema Linfático',
    academy_level3_topics: 'Circulação Linfática,Funções Bio Linfáticas incl. Sistema Imunitário,Exame Linfático,Tratamento Linfático',
    academy_level4_title: 'Cardíaco / Fases de Tratamento',
    academy_level4_topics: 'Sistema Cardíaco,Fases de Cura,Fases de Tratamento,Protocolos e Aplicação',
    academy_level5_title: 'Performance e Recuperação',
    academy_level5_topics: 'Energia Animal,Energia dos Órgãos,Energia Celular,Testosterona / Hormonas',
    academy_level6_title: 'Curso Prático',
    academy_level6_topics: 'Técnicas de Exame,Desenho de Protocolos,Aplicação de Discos,Avaliação Final',
    academy_impl_title: 'Implementação com Sucesso',
    academy_impl_subtitle: 'Tudo o que precisa para uma implementação bem-sucedida da ANF Animal Therapy®.',
    academy_impl_tools: 'Aplicação de Protocolos,Downloads e Modelos,Auto-estudo e Questionários,Treino de Palpação,Mentoria e Fóruns,Casos e Resultados,Exame e Certificado',
    academy_join_title: 'Quem Pode Participar?',
    academy_join_subtitle: 'Bem-vindo à Educação ANF Animal Therapy®.',
    academy_join_roles: 'Veterinários,Fisioterapeutas Equinos,Osteopatas Equinos,Acupunturistas Equinos,Massagistas Equinos,Quiropráticos Equinos,Tratadores,Treinadores de Animais',
    cookie_text: 'Este website utiliza cookies para melhorar a sua experiência. Ao continuar a navegar, concorda com a nossa utilização de cookies conforme a normativa europeia.',
    cookie_accept: 'Aceitar',
    cookie_reject: 'Rejeitar',
    cookie_privacy_link: 'Política de Privacidade',
  },
  en: {
    nav_home: 'Home',
    nav_about: 'About Us',
    nav_services: 'Services',
    nav_contact: 'Contact',
    nav_facilities: 'Facilities',
    nav_academy: 'Academy',
    facilities_title: 'Our Facilities',
    facilities_subtitle: 'Discover our privileged space in Porches, Algarve — an environment of excellence for animal well-being.',
    facilities_video_title: 'Clinic Video',
    hero_title: 'Animal Frequency Therapy Clinic & Academy',
    hero_subtitle: 'Revolutionary ANF technology for your animal\'s well-being. Non-invasive treatments with therapeutic frequency discs.',
    hero_cta_booking: 'Book Appointment',
    hero_cta_academy: 'Professional Training',
    about_title: 'About ANFAnimal Portugal',
    about_subtitle: 'Excellence in animal therapy with cutting-edge technology',
    about_description: 'ANFAnimal Portugal is the leading frequency therapy clinic for animals in the Iberian Peninsula. We combine patented ANF technology with a team of highly qualified professionals to offer the best treatments for your animal.',
    about_academy_title: 'Training Academy',
    about_academy_desc: 'We train veterinary professionals in ANF Animal technology. Certified courses with supervised clinical practice.',
    about_academy_button: 'Learn More',
    about_stat_pain: '+50%',
    about_stat_pain_label: 'Pain Reduction',
    about_stat_discs: '30+',
    about_stat_discs_label: 'Therapeutic Discs',
    about_stat_countries: '40+',
    about_stat_countries_label: 'Countries',
    about_stat_years: '10+',
    about_stat_years_label: 'Years of Research',
    about_device_title: 'ANF Medical Device',
    about_device_desc: 'Discover the patented ANF Animal technology — therapeutic frequency discs that revolutionize animal treatment.',
    about_device_button: 'View Medical Device',
    popup_back: 'Back to Website',
    services_title: 'Services & Treatments',
    services_subtitle: 'Non-invasive frequency therapies for your animal',
    service_pain: 'Pain Management',
    service_pain_desc: 'Effective treatment of chronic and acute pain through ANF frequency discs. Visible results from the first session.',
    service_neuro: 'Neurological Therapy',
    service_neuro_desc: 'Specific frequencies for neurological conditions, improving nerve communication and mobility.',
    service_inflammation: 'Anti-Inflammation',
    service_inflammation_desc: 'Natural inflammation reduction without medication, using specific therapeutic frequencies.',
    service_recovery: 'Post-Surgical Recovery',
    service_recovery_desc: 'Acceleration of recovery after surgery, with discs that promote cellular regeneration.',
    services_how_title: 'How It Works',
    services_step1: 'Assessment',
    services_step1_desc: 'Complete examination of the animal to identify treatment areas.',
    services_step2: 'Application',
    services_step2_desc: 'Strategic placement of ANF frequency discs.',
    services_step3: 'Results',
    services_step3_desc: 'Progressive improvement with follow-up sessions.',
    contact_title: 'Contact Us',
    contact_subtitle: 'Book your appointment or request more information',
    contact_name: 'Name',
    contact_email: 'Email',
    contact_phone: 'Phone',
    contact_message: 'Message',
    contact_send: 'Send Message',
    contact_address: 'Porches, Algarve',
    contact_schedule: 'Schedule',
    contact_schedule_value: 'Mon-Fri: 9am-6pm | Sat: 9am-1pm',
    footer_rights: 'All rights reserved.',
    footer_privacy: 'Privacy Policy',
    footer_terms: 'Terms & Conditions',
    footer_tagline: 'Premium Animal Frequency Therapy',
    privacy_last_updated: 'Last updated: March 2026',
    privacy_section1_title: 'Information We Collect',
    privacy_section1_text: 'We collect personal information you voluntarily provide when contacting us, including name, email, phone number and details about your pet.',
    privacy_section2_title: 'Use of Information',
    privacy_section2_text: 'We use your information to provide our ANF Animal therapy services, schedule appointments, communicate with you and improve our services.',
    privacy_section3_title: 'Data Protection',
    privacy_section3_text: 'We implement appropriate security measures to protect your personal information against unauthorized access, alteration or destruction.',
    privacy_section4_title: 'Cookies',
    privacy_section4_text: 'Our website uses cookies to enhance your browsing experience. You can configure your browser to refuse cookies.',
    privacy_section5_title: 'Your Rights',
    privacy_section5_text: 'Under GDPR, you have the right to access, rectify, delete and port your personal data. Contact us to exercise these rights.',
    privacy_section6_title: 'Contact',
    privacy_section6_text: 'For privacy questions, contact us at geral@anfanimalclinicportugal.com, by phone at +351 928 281 833, or visit our clinic at R. Porches Velho, 8400-490 Porches.',
    terms_last_updated: 'Last updated: March 2026',
    terms_section1_title: 'Acceptance of Terms',
    terms_section1_text: 'By accessing and using the ANFAnimal Portugal website, you agree to these terms and conditions. If you do not agree, do not use the website.',
    terms_section2_title: 'Services',
    terms_section2_text: 'ANFAnimal Portugal offers non-invasive frequency therapy for animals using ANF technology. Results may vary and we do not guarantee specific outcomes.',
    terms_section3_title: 'Intellectual Property',
    terms_section3_text: 'All website content, including text, images, logos and design, is the property of ANFAnimal Portugal and is protected by copyright.',
    terms_section4_title: 'Limitation of Liability',
    terms_section4_text: 'ANFAnimal Portugal is not liable for indirect damages resulting from the use of our services. ANF therapy is complementary and does not replace conventional veterinary treatment.',
    terms_section5_title: 'Cancellations and Refunds',
    terms_section5_text: 'Appointments may be cancelled 24 hours in advance at no cost. Late cancellations may be subject to a fee.',
    terms_section6_title: 'Applicable Law',
    terms_section6_text: 'These terms are governed by Portuguese law. Any dispute shall be submitted to the courts of Portimão, Portugal.',
    academy_hero_title: 'Training Academy',
    academy_hero_subtitle: 'Find the root cause of any pain, injury and/or pathology and understand animal anatomy as a unit. 6 education levels to master ANF Animal Therapy®.',
    academy_pillars_title: 'Core Pillars',
    academy_pillar1_title: 'Neurological Function',
    academy_pillar1_desc: 'The complexity and functions of the nervous system which allows the animal to self-heal with the use of ANF Animal Therapy®.',
    academy_pillar2_title: 'Biochemistry & Resources',
    academy_pillar2_desc: 'The role of cells, molecules, hormones, and vitamins, … and their interactions which are necessary to facilitate the fusions and frequencies required to repair the dysfunctions.',
    academy_pillar3_title: 'Neuro Frequency Medicine',
    academy_pillar3_desc: 'Normalize inflammation and neurological dysfunction with the most effective Tech Device using revolutionary and advanced novel technology with neuro frequencies that allows the body to self-repair without side effects or drugs.',
    academy_pillar4_title: 'Clinical Training & Implementation',
    academy_pillar4_desc: 'Learn Neurological Examination techniques to gain outstanding palpatory and practical skills within the musculoskeletal, nervous, lymphatic, digestive and endocrine systems.',
    academy_curriculum: 'Curriculum',
    academy_edu_title: '6 Education Levels',
    academy_edu_subtitle: 'A structured path to mastering ANF Animal Therapy®',
    academy_level1_title: 'Pain & Inflammation',
    academy_level1_topics: 'Frequencies & Methodology,Pain & Inflammation,Antioxidants,Protocols & Application',
    academy_level2_title: 'Nervous System',
    academy_level2_topics: 'Nervous System,Species Comparison,Treatment Plans,Protocols & Application',
    academy_level3_title: 'Lymphatic System',
    academy_level3_topics: 'Lymph Circulation,Lymph Bio Functions incl. Immune System,Lymph Examination,Lymph Treatment',
    academy_level4_title: 'Cardiac / Treatment Phases',
    academy_level4_topics: 'Cardiac System,Phases of Healing,Phases of Treatment,Protocols & Application',
    academy_level5_title: 'Performance & Recovery',
    academy_level5_topics: 'Animal Energy,Organ Energy,Cellular Energy,Testosterone / Hormones',
    academy_level6_title: 'Practical Course',
    academy_level6_topics: 'Examination Techniques,Protocol Design,Disc Application,Final Assessment',
    academy_impl_title: 'Successful Implementation',
    academy_impl_subtitle: 'All you need for successful implementation of ANF Animal Therapy®.',
    academy_impl_tools: 'Protocol Application,Download & Templates,Self-study & Quizzes,Palpation Training,Mentoring & Forums,Cases & Results,Exam & Certificate',
    academy_join_title: 'Who Can Join?',
    academy_join_subtitle: 'Welcome to ANF Animal Therapy® Education.',
    academy_join_roles: 'Veterinarians,Equine Physios,Equine Osteopaths,Equine Acupuncturists,Equine Massage Therapists,Equine Chiropractors,Groomers,Animal Trainers',
    cookie_text: 'This website uses cookies to improve your experience. By continuing to browse, you agree to our use of cookies in accordance with European regulations.',
    cookie_accept: 'Accept',
    cookie_reject: 'Reject',
    cookie_privacy_link: 'Privacy Policy',
  },
  es: {
    nav_home: 'Inicio',
    nav_about: 'Sobre Nosotros',
    nav_services: 'Servicios',
    nav_contact: 'Contacto',
    nav_facilities: 'Instalaciones',
    nav_academy: 'Academia',
    facilities_title: 'Nuestras Instalaciones',
    facilities_subtitle: 'Descubre nuestro espacio privilegiado en Porches, Algarve — un entorno de excelencia para el bienestar animal.',
    facilities_video_title: 'Vídeo de la Clínica',
    hero_title: 'Clínica & Academia de Terapia de Frecuencia Animal',
    hero_subtitle: 'Tecnología ANF revolucionaria para el bienestar de tu animal. Tratamientos no invasivos con discos de frecuencia terapéutica.',
    hero_cta_booking: 'Reservar Cita',
    hero_cta_academy: 'Formación Profesional',
    about_title: 'Sobre ANFAnimal Portugal',
    about_subtitle: 'Excelencia en terapia animal con tecnología de vanguardia',
    about_description: 'ANFAnimal Portugal es la clínica de referencia en terapia de frecuencia para animales en la Península Ibérica. Combinamos la tecnología patentada ANF con un equipo de profesionales altamente cualificados.',
    about_academy_title: 'Academia de Formación',
    about_academy_desc: 'Formamos profesionales veterinarios en tecnología ANF Animal. Cursos certificados con práctica clínica supervisada.',
    about_academy_button: 'Saber Más',
    about_stat_pain: '+50%',
    about_stat_pain_label: 'Reducción del Dolor',
    about_stat_discs: '30+',
    about_stat_discs_label: 'Discos Terapéuticos',
    about_stat_countries: '40+',
    about_stat_countries_label: 'Países',
    about_stat_years: '10+',
    about_stat_years_label: 'Años de Investigación',
    about_device_title: 'Dispositivo Médico ANF',
    about_device_desc: 'Descubre la tecnología patentada ANF Animal — discos de frecuencia terapéutica que revolucionan el tratamiento animal.',
    about_device_button: 'Ver Dispositivo Médico',
    popup_back: 'Volver a la Web',
    services_title: 'Servicios & Tratamientos',
    services_subtitle: 'Terapias de frecuencia no invasivas para tu animal',
    service_pain: 'Gestión del Dolor',
    service_pain_desc: 'Tratamiento eficaz del dolor crónico y agudo mediante discos de frecuencia ANF.',
    service_neuro: 'Terapia Neurológica',
    service_neuro_desc: 'Frecuencias específicas para condiciones neurológicas, mejorando la comunicación nerviosa.',
    service_inflammation: 'Anti-Inflamación',
    service_inflammation_desc: 'Reducción natural de la inflamación sin medicamentos, usando frecuencias terapéuticas.',
    service_recovery: 'Recuperación Post-Quirúrgica',
    service_recovery_desc: 'Aceleración de la recuperación tras cirugías con discos que promueven la regeneración celular.',
    services_how_title: 'Cómo Funciona',
    services_step1: 'Evaluación',
    services_step1_desc: 'Examen completo del animal para identificar áreas de tratamiento.',
    services_step2: 'Aplicación',
    services_step2_desc: 'Colocación estratégica de los discos de frecuencia ANF.',
    services_step3: 'Resultados',
    services_step3_desc: 'Mejora progresiva con sesiones de seguimiento.',
    contact_title: 'Contáctenos',
    contact_subtitle: 'Reserve su cita o solicite más información',
    contact_name: 'Nombre',
    contact_email: 'Email',
    contact_phone: 'Teléfono',
    contact_message: 'Mensaje',
    contact_send: 'Enviar Mensaje',
    contact_address: 'Porches, Algarve',
    contact_schedule: 'Horario',
    contact_schedule_value: 'Lun-Vie: 9h-18h | Sáb: 9h-13h',
    footer_rights: 'Todos los derechos reservados.',
    footer_privacy: 'Política de Privacidad',
    footer_terms: 'Términos y Condiciones',
    footer_tagline: 'Terapia de Frecuencia Animal Premium',
    privacy_last_updated: 'Última actualización: Marzo 2026',
    privacy_section1_title: 'Información que Recopilamos',
    privacy_section1_text: 'Recopilamos información personal que nos proporciona voluntariamente al contactarnos, incluyendo nombre, email, teléfono y detalles sobre su mascota.',
    privacy_section2_title: 'Uso de la Información',
    privacy_section2_text: 'Utilizamos su información para prestar nuestros servicios de terapia ANF Animal, programar citas, comunicarnos con usted y mejorar nuestros servicios.',
    privacy_section3_title: 'Protección de Datos',
    privacy_section3_text: 'Implementamos medidas de seguridad adecuadas para proteger su información personal contra acceso no autorizado, alteración o destrucción.',
    privacy_section4_title: 'Cookies',
    privacy_section4_text: 'Nuestro sitio web utiliza cookies para mejorar su experiencia de navegación. Puede configurar su navegador para rechazar cookies.',
    privacy_section5_title: 'Sus Derechos',
    privacy_section5_text: 'Bajo el RGPD, tiene derecho a acceder, rectificar, eliminar y portar sus datos personales. Contáctenos para ejercer estos derechos.',
    privacy_section6_title: 'Contacto',
    privacy_section6_text: 'Para consultas sobre privacidad, contáctenos en geral@anfanimalclinicportugal.com, por teléfono al +351 928 281 833, o visite nuestra clínica en R. Porches Velho, 8400-490 Porches.',
    terms_last_updated: 'Última actualización: Marzo 2026',
    terms_section1_title: 'Aceptación de los Términos',
    terms_section1_text: 'Al acceder y utilizar el sitio web de ANFAnimal Portugal, acepta estos términos y condiciones. Si no está de acuerdo, no utilice el sitio web.',
    terms_section2_title: 'Servicios',
    terms_section2_text: 'ANFAnimal Portugal ofrece terapia de frecuencia no invasiva para animales mediante tecnología ANF. Los resultados pueden variar y no garantizamos resultados específicos.',
    terms_section3_title: 'Propiedad Intelectual',
    terms_section3_text: 'Todo el contenido del sitio web, incluyendo textos, imágenes, logotipos y diseño, es propiedad de ANFAnimal Portugal y está protegido por derechos de autor.',
    terms_section4_title: 'Limitación de Responsabilidad',
    terms_section4_text: 'ANFAnimal Portugal no se responsabiliza por daños indirectos derivados del uso de nuestros servicios. La terapia ANF es complementaria y no sustituye el tratamiento veterinario convencional.',
    terms_section5_title: 'Cancelaciones y Reembolsos',
    terms_section5_text: 'Las citas pueden cancelarse con 24 horas de antelación sin coste. Las cancelaciones tardías pueden estar sujetas a un cargo.',
    terms_section6_title: 'Ley Aplicable',
    terms_section6_text: 'Estos términos se rigen por la legislación portuguesa. Cualquier litigio será sometido a los tribunales de Portimão, Portugal.',
    academy_hero_title: 'Academia de Formación',
    academy_hero_subtitle: 'Encuentra la causa raíz de cualquier dolor, lesión y/o patología y comprende la anatomía animal como una unidad. 6 niveles educativos para dominar la ANF Animal Therapy®.',
    academy_pillars_title: 'Pilares Fundamentales',
    academy_pillar1_title: 'Función Neurológica',
    academy_pillar1_desc: 'La complejidad y funciones del sistema nervioso que permite al animal la autocuración con el uso de ANF Animal Therapy®.',
    academy_pillar2_title: 'Bioquímica y Recursos',
    academy_pillar2_desc: 'El papel de las células, moléculas, hormonas y vitaminas y sus interacciones necesarias para facilitar las fusiones y frecuencias requeridas para reparar las disfunciones.',
    academy_pillar3_title: 'Medicina de Neurofrecuencia',
    academy_pillar3_desc: 'Normalizar la inflamación y la disfunción neurológica con el dispositivo tecnológico más eficaz, utilizando tecnología revolucionaria y avanzada con neurofrecuencias que permite al cuerpo autorrepararse sin efectos secundarios ni medicamentos.',
    academy_pillar4_title: 'Formación Clínica e Implementación',
    academy_pillar4_desc: 'Aprende técnicas de examen neurológico para obtener habilidades palpatorias y prácticas excepcionales en los sistemas musculoesquelético, nervioso, linfático, digestivo y endocrino.',
    academy_curriculum: 'Plan de Estudios',
    academy_edu_title: '6 Niveles Educativos',
    academy_edu_subtitle: 'Un camino estructurado para dominar la ANF Animal Therapy®',
    academy_level1_title: 'Dolor e Inflamación',
    academy_level1_topics: 'Frecuencias y Metodología,Dolor e Inflamación,Antioxidantes,Protocolos y Aplicación',
    academy_level2_title: 'Sistema Nervioso',
    academy_level2_topics: 'Sistema Nervioso,Comparación de Especies,Planes de Tratamiento,Protocolos y Aplicación',
    academy_level3_title: 'Sistema Linfático',
    academy_level3_topics: 'Circulación Linfática,Funciones Bio Linfáticas incl. Sistema Inmunitario,Examen Linfático,Tratamiento Linfático',
    academy_level4_title: 'Cardíaco / Fases de Tratamiento',
    academy_level4_topics: 'Sistema Cardíaco,Fases de Curación,Fases de Tratamiento,Protocolos y Aplicación',
    academy_level5_title: 'Rendimiento y Recuperación',
    academy_level5_topics: 'Energía Animal,Energía de Órganos,Energía Celular,Testosterona / Hormonas',
    academy_level6_title: 'Curso Práctico',
    academy_level6_topics: 'Técnicas de Examen,Diseño de Protocolos,Aplicación de Discos,Evaluación Final',
    academy_impl_title: 'Implementación Exitosa',
    academy_impl_subtitle: 'Todo lo que necesitas para una implementación exitosa de ANF Animal Therapy®.',
    academy_impl_tools: 'Aplicación de Protocolos,Descargas y Plantillas,Autoestudio y Cuestionarios,Entrenamiento de Palpación,Mentoría y Foros,Casos y Resultados,Examen y Certificado',
    academy_join_title: '¿Quién Puede Participar?',
    academy_join_subtitle: 'Bienvenido a la Educación ANF Animal Therapy®.',
    academy_join_roles: 'Veterinarios,Fisioterapeutas Equinos,Osteópatas Equinos,Acupunturistas Equinos,Masajistas Equinos,Quiroprácticos Equinos,Cuidadores,Entrenadores de Animales',
    cookie_text: 'Este sitio web utiliza cookies para mejorar su experiencia. Al continuar navegando, acepta nuestro uso de cookies conforme a la normativa europea.',
    cookie_accept: 'Aceptar',
    cookie_reject: 'Rechazar',
    cookie_privacy_link: 'Política de Privacidad',
  },
  nl: {
    nav_home: 'Home',
    nav_about: 'Over Ons',
    nav_services: 'Diensten',
    nav_contact: 'Contact',
    nav_facilities: 'Faciliteiten',
    nav_academy: 'Academie',
    facilities_title: 'Onze Faciliteiten',
    facilities_subtitle: 'Ontdek onze bevoorrechte ruimte in Porches, Algarve — een omgeving van uitmuntendheid voor dierenwelzijn.',
    facilities_video_title: 'Kliniek Video',
    hero_title: 'Dierlijke Frequentietherapie Kliniek & Academie',
    hero_subtitle: 'Revolutionaire ANF-technologie voor het welzijn van uw dier. Niet-invasieve behandelingen met therapeutische frequentieschijven.',
    hero_cta_booking: 'Afspraak Maken',
    hero_cta_academy: 'Professionele Opleiding',
    about_title: 'Over ANFAnimal Portugal',
    about_subtitle: 'Uitmuntendheid in diertherapie met geavanceerde technologie',
    about_description: 'ANFAnimal Portugal is de toonaangevende frequentietherapiekliniek voor dieren op het Iberisch Schiereiland. Wij combineren gepatenteerde ANF-technologie met hoogopgeleide professionals.',
    about_academy_title: 'Opleidingsacademie',
    about_academy_desc: 'Wij leiden veterinaire professionals op in ANF Animal technologie. Gecertificeerde cursussen met begeleide klinische praktijk.',
    about_academy_button: 'Meer Info',
    about_stat_pain: '+50%',
    about_stat_pain_label: 'Pijnvermindering',
    about_stat_discs: '30+',
    about_stat_discs_label: 'Therapeutische Schijven',
    about_stat_countries: '40+',
    about_stat_countries_label: 'Landen',
    about_stat_years: '10+',
    about_stat_years_label: 'Jaren Onderzoek',
    about_device_title: 'ANF Medisch Apparaat',
    about_device_desc: 'Ontdek de gepatenteerde ANF Animal technologie — therapeutische frequentieschijven die dierbehandeling revolutioneren.',
    about_device_button: 'Bekijk Medisch Apparaat',
    popup_back: 'Terug naar Website',
    services_title: 'Diensten & Behandelingen',
    services_subtitle: 'Niet-invasieve frequentietherapieën voor uw dier',
    service_pain: 'Pijnbehandeling',
    service_pain_desc: 'Effectieve behandeling van chronische en acute pijn met ANF-frequentieschijven.',
    service_neuro: 'Neurologische Therapie',
    service_neuro_desc: 'Specifieke frequenties voor neurologische aandoeningen, verbetering van zenuwcommunicatie.',
    service_inflammation: 'Ontstekingsremmend',
    service_inflammation_desc: 'Natuurlijke ontstekingsvermindering zonder medicijnen met therapeutische frequenties.',
    service_recovery: 'Postoperatief Herstel',
    service_recovery_desc: 'Versnelling van herstel na operaties met schijven die celregeneratie bevorderen.',
    services_how_title: 'Hoe Het Werkt',
    services_step1: 'Beoordeling',
    services_step1_desc: 'Volledig onderzoek van het dier om behandelgebieden te identificeren.',
    services_step2: 'Toepassing',
    services_step2_desc: 'Strategische plaatsing van ANF-frequentieschijven.',
    services_step3: 'Resultaten',
    services_step3_desc: 'Progressieve verbetering met vervolgbehandelingen.',
    contact_title: 'Neem Contact Op',
    contact_subtitle: 'Maak een afspraak of vraag meer informatie aan',
    contact_name: 'Naam',
    contact_email: 'E-mail',
    contact_phone: 'Telefoon',
    contact_message: 'Bericht',
    contact_send: 'Bericht Versturen',
    contact_address: 'Porches, Algarve',
    contact_schedule: 'Openingstijden',
    contact_schedule_value: 'Ma-Vr: 9-18u | Za: 9-13u',
    footer_rights: 'Alle rechten voorbehouden.',
    footer_privacy: 'Privacybeleid',
    footer_terms: 'Algemene Voorwaarden',
    footer_tagline: 'Premium Dierlijke Frequentietherapie',
    privacy_last_updated: 'Laatst bijgewerkt: Maart 2026',
    privacy_section1_title: 'Informatie die Wij Verzamelen',
    privacy_section1_text: 'Wij verzamelen persoonlijke informatie die u vrijwillig verstrekt wanneer u contact met ons opneemt, waaronder naam, e-mail, telefoonnummer en details over uw huisdier.',
    privacy_section2_title: 'Gebruik van Informatie',
    privacy_section2_text: 'Wij gebruiken uw informatie om onze ANF Animal therapiediensten te verlenen, afspraken te plannen, met u te communiceren en onze diensten te verbeteren.',
    privacy_section3_title: 'Gegevensbescherming',
    privacy_section3_text: 'Wij implementeren passende beveiligingsmaatregelen om uw persoonlijke informatie te beschermen tegen ongeautoriseerde toegang, wijziging of vernietiging.',
    privacy_section4_title: 'Cookies',
    privacy_section4_text: 'Onze website gebruikt cookies om uw browse-ervaring te verbeteren. U kunt uw browser configureren om cookies te weigeren.',
    privacy_section5_title: 'Uw Rechten',
    privacy_section5_text: 'Onder de AVG heeft u het recht op toegang tot, rectificatie, verwijdering en overdracht van uw persoonsgegevens. Neem contact met ons op om deze rechten uit te oefenen.',
    privacy_section6_title: 'Contact',
    privacy_section6_text: 'Voor privacyvragen kunt u contact met ons opnemen via geral@anfanimalclinicportugal.com, telefonisch op +351 928 281 833, of bezoek onze kliniek op R. Porches Velho, 8400-490 Porches.',
    terms_last_updated: 'Laatst bijgewerkt: Maart 2026',
    terms_section1_title: 'Aanvaarding van Voorwaarden',
    terms_section1_text: 'Door de website van ANFAnimal Portugal te bezoeken en te gebruiken, gaat u akkoord met deze voorwaarden. Als u niet akkoord gaat, gebruik de website dan niet.',
    terms_section2_title: 'Diensten',
    terms_section2_text: 'ANFAnimal Portugal biedt niet-invasieve frequentietherapie voor dieren met ANF-technologie. Resultaten kunnen variëren en wij garanderen geen specifieke resultaten.',
    terms_section3_title: 'Intellectueel Eigendom',
    terms_section3_text: 'Alle website-inhoud, inclusief teksten, afbeeldingen, logo\'s en ontwerp, is eigendom van ANFAnimal Portugal en wordt beschermd door auteursrecht.',
    terms_section4_title: 'Beperking van Aansprakelijkheid',
    terms_section4_text: 'ANFAnimal Portugal is niet aansprakelijk voor indirecte schade als gevolg van het gebruik van onze diensten. ANF-therapie is aanvullend en vervangt geen conventionele veterinaire behandeling.',
    terms_section5_title: 'Annuleringen en Terugbetalingen',
    terms_section5_text: 'Afspraken kunnen 24 uur van tevoren kosteloos worden geannuleerd. Late annuleringen kunnen onderhevig zijn aan kosten.',
    terms_section6_title: 'Toepasselijk Recht',
    terms_section6_text: 'Deze voorwaarden worden beheersd door Portugees recht. Elk geschil wordt voorgelegd aan de rechtbanken van Portimão, Portugal.',
    academy_hero_title: 'Opleidingsacademie',
    academy_hero_subtitle: 'Vind de oorzaak van elke pijn, blessure en/of pathologie en begrijp de dieranatomie als geheel. 6 opleidingsniveaus om ANF Animal Therapy® te beheersen.',
    academy_pillars_title: 'Kernpijlers',
    academy_pillar1_title: 'Neurologische Functie',
    academy_pillar1_desc: 'De complexiteit en functies van het zenuwstelsel waardoor het dier zichzelf kan genezen met behulp van ANF Animal Therapy®.',
    academy_pillar2_title: 'Biochemie & Hulpbronnen',
    academy_pillar2_desc: 'De rol van cellen, moleculen, hormonen en vitaminen en hun interacties die nodig zijn om de fusies en frequenties te faciliteren die nodig zijn om disfuncties te herstellen.',
    academy_pillar3_title: 'Neurofrequentie Geneeskunde',
    academy_pillar3_desc: 'Normaliseer ontsteking en neurologische disfunctie met het meest effectieve technologische apparaat met revolutionaire en geavanceerde neurofrequenties waarmee het lichaam zichzelf kan herstellen zonder bijwerkingen of medicijnen.',
    academy_pillar4_title: 'Klinische Opleiding & Implementatie',
    academy_pillar4_desc: 'Leer neurologische onderzoekstechnieken voor uitstekende palpatoire en praktische vaardigheden in het musculoskeletale, zenuw-, lymfe-, spijsverterings- en endocriene systeem.',
    academy_curriculum: 'Curriculum',
    academy_edu_title: '6 Opleidingsniveaus',
    academy_edu_subtitle: 'Een gestructureerd pad naar het beheersen van ANF Animal Therapy®',
    academy_level1_title: 'Pijn & Ontsteking',
    academy_level1_topics: 'Frequenties & Methodologie,Pijn & Ontsteking,Antioxidanten,Protocollen & Toepassing',
    academy_level2_title: 'Zenuwstelsel',
    academy_level2_topics: 'Zenuwstelsel,Soortenvergelijking,Behandelplannen,Protocollen & Toepassing',
    academy_level3_title: 'Lymfestelsel',
    academy_level3_topics: 'Lymfecirculatie,Lymfe Biofuncties incl. Immuunsysteem,Lymfe-onderzoek,Lymfebehandeling',
    academy_level4_title: 'Cardiaal / Behandelfasen',
    academy_level4_topics: 'Cardiaal Systeem,Genezingsfasen,Behandelfasen,Protocollen & Toepassing',
    academy_level5_title: 'Prestatie & Herstel',
    academy_level5_topics: 'Dierlijke Energie,Orgaanenergie,Cellulaire Energie,Testosteron / Hormonen',
    academy_level6_title: 'Praktijkcursus',
    academy_level6_topics: 'Onderzoekstechnieken,Protocolontwerp,Schijftoepassing,Eindbeoordeling',
    academy_impl_title: 'Succesvolle Implementatie',
    academy_impl_subtitle: 'Alles wat u nodig heeft voor een succesvolle implementatie van ANF Animal Therapy®.',
    academy_impl_tools: 'Protocoltoepassing,Downloads & Sjablonen,Zelfstudie & Quizzen,Palpatie Training,Mentoring & Forums,Cases & Resultaten,Examen & Certificaat',
    academy_join_title: 'Wie Kan Deelnemen?',
    academy_join_subtitle: 'Welkom bij ANF Animal Therapy® Educatie.',
    academy_join_roles: 'Dierenartsen,Paardenfysiotherapeuten,Paardenosteopaten,Paardenacupuncturisten,Paardenmassagetherapeuten,Paardenchiropractors,Verzorgers,Dierentrainers',
    cookie_text: 'Deze website maakt gebruik van cookies om uw ervaring te verbeteren. Door verder te bladeren, gaat u akkoord met ons cookiegebruik conform de Europese regelgeving.',
    cookie_accept: 'Accepteren',
    cookie_reject: 'Weigeren',
    cookie_privacy_link: 'Privacybeleid',
  },
  de: {
    nav_home: 'Startseite',
    nav_about: 'Über Uns',
    nav_services: 'Leistungen',
    nav_contact: 'Kontakt',
    nav_facilities: 'Einrichtungen',
    nav_academy: 'Akademie',
    facilities_title: 'Unsere Einrichtungen',
    facilities_subtitle: 'Entdecken Sie unseren privilegierten Standort in Porches, Algarve — ein Umfeld der Exzellenz für das Tierwohl.',
    facilities_video_title: 'Klinik-Video',
    hero_title: 'Tier-Frequenztherapie Klinik & Akademie',
    hero_subtitle: 'Revolutionäre ANF-Technologie für das Wohlbefinden Ihres Tieres. Nicht-invasive Behandlungen mit therapeutischen Frequenzscheiben.',
    hero_cta_booking: 'Termin Buchen',
    hero_cta_academy: 'Professionelle Ausbildung',
    about_title: 'Über ANFAnimal Portugal',
    about_subtitle: 'Exzellenz in der Tiertherapie mit modernster Technologie',
    about_description: 'ANFAnimal Portugal ist die führende Frequenztherapie-Klinik für Tiere auf der Iberischen Halbinsel. Wir kombinieren patentierte ANF-Technologie mit hochqualifizierten Fachleuten.',
    about_academy_title: 'Ausbildungsakademie',
    about_academy_desc: 'Wir bilden Tierärzte in ANF Animal Technologie aus. Zertifizierte Kurse mit betreuter klinischer Praxis.',
    about_academy_button: 'Mehr Erfahren',
    about_stat_pain: '+50%',
    about_stat_pain_label: 'Schmerzreduktion',
    about_stat_discs: '30+',
    about_stat_discs_label: 'Therapeutische Scheiben',
    about_stat_countries: '40+',
    about_stat_countries_label: 'Länder',
    about_stat_years: '10+',
    about_stat_years_label: 'Jahre Forschung',
    about_device_title: 'ANF Medizinprodukt',
    about_device_desc: 'Entdecken Sie die patentierte ANF Animal Technologie — therapeutische Frequenzscheiben, die die Tierbehandlung revolutionieren.',
    about_device_button: 'Medizinprodukt ansehen',
    popup_back: 'Zurück zur Website',
    services_title: 'Leistungen & Behandlungen',
    services_subtitle: 'Nicht-invasive Frequenztherapien für Ihr Tier',
    service_pain: 'Schmerzmanagement',
    service_pain_desc: 'Effektive Behandlung chronischer und akuter Schmerzen mit ANF-Frequenzscheiben.',
    service_neuro: 'Neurologische Therapie',
    service_neuro_desc: 'Spezifische Frequenzen für neurologische Erkrankungen, Verbesserung der Nervenkommunikation.',
    service_inflammation: 'Entzündungshemmung',
    service_inflammation_desc: 'Natürliche Entzündungsreduzierung ohne Medikamente mit therapeutischen Frequenzen.',
    service_recovery: 'Postoperative Erholung',
    service_recovery_desc: 'Beschleunigung der Erholung nach Operationen mit Scheiben zur Zellregeneration.',
    services_how_title: 'So Funktioniert Es',
    services_step1: 'Bewertung',
    services_step1_desc: 'Vollständige Untersuchung des Tieres zur Identifizierung der Behandlungsbereiche.',
    services_step2: 'Anwendung',
    services_step2_desc: 'Strategische Platzierung der ANF-Frequenzscheiben.',
    services_step3: 'Ergebnisse',
    services_step3_desc: 'Progressive Verbesserung mit Folgebehandlungen.',
    contact_title: 'Kontaktieren Sie Uns',
    contact_subtitle: 'Buchen Sie Ihren Termin oder fordern Sie weitere Informationen an',
    contact_name: 'Name',
    contact_email: 'E-Mail',
    contact_phone: 'Telefon',
    contact_message: 'Nachricht',
    contact_send: 'Nachricht Senden',
    contact_address: 'Porches, Algarve',
    contact_schedule: 'Öffnungszeiten',
    contact_schedule_value: 'Mo-Fr: 9-18 Uhr | Sa: 9-13 Uhr',
    footer_rights: 'Alle Rechte vorbehalten.',
    footer_privacy: 'Datenschutz',
    footer_terms: 'AGB',
    footer_tagline: 'Premium Tier-Frequenztherapie',
    privacy_last_updated: 'Letzte Aktualisierung: März 2026',
    privacy_section1_title: 'Informationen, die wir erheben',
    privacy_section1_text: 'Wir erheben personenbezogene Daten, die Sie uns freiwillig bei der Kontaktaufnahme mitteilen, einschließlich Name, E-Mail, Telefonnummer und Angaben zu Ihrem Haustier.',
    privacy_section2_title: 'Verwendung der Informationen',
    privacy_section2_text: 'Wir verwenden Ihre Daten, um unsere ANF Animal Therapiedienste zu erbringen, Termine zu vereinbaren, mit Ihnen zu kommunizieren und unsere Dienste zu verbessern.',
    privacy_section3_title: 'Datenschutz',
    privacy_section3_text: 'Wir setzen angemessene Sicherheitsmaßnahmen ein, um Ihre persönlichen Daten vor unbefugtem Zugriff, Veränderung oder Zerstörung zu schützen.',
    privacy_section4_title: 'Cookies',
    privacy_section4_text: 'Unsere Website verwendet Cookies, um Ihr Surferlebnis zu verbessern. Sie können Ihren Browser so konfigurieren, dass Cookies abgelehnt werden.',
    privacy_section5_title: 'Ihre Rechte',
    privacy_section5_text: 'Gemäß DSGVO haben Sie das Recht auf Zugang, Berichtigung, Löschung und Übertragung Ihrer personenbezogenen Daten. Kontaktieren Sie uns, um diese Rechte auszuüben.',
    privacy_section6_title: 'Kontakt',
    privacy_section6_text: 'Bei Fragen zum Datenschutz kontaktieren Sie uns unter geral@anfanimalclinicportugal.com, telefonisch unter +351 928 281 833, oder besuchen Sie unsere Klinik in R. Porches Velho, 8400-490 Porches.',
    terms_last_updated: 'Letzte Aktualisierung: März 2026',
    terms_section1_title: 'Annahme der Bedingungen',
    terms_section1_text: 'Durch den Zugriff auf und die Nutzung der Website von ANFAnimal Portugal stimmen Sie diesen Geschäftsbedingungen zu. Wenn Sie nicht einverstanden sind, nutzen Sie die Website nicht.',
    terms_section2_title: 'Dienstleistungen',
    terms_section2_text: 'ANFAnimal Portugal bietet nicht-invasive Frequenztherapie für Tiere mit ANF-Technologie an. Die Ergebnisse können variieren und wir garantieren keine bestimmten Ergebnisse.',
    terms_section3_title: 'Geistiges Eigentum',
    terms_section3_text: 'Alle Website-Inhalte, einschließlich Texte, Bilder, Logos und Design, sind Eigentum von ANFAnimal Portugal und urheberrechtlich geschützt.',
    terms_section4_title: 'Haftungsbeschränkung',
    terms_section4_text: 'ANFAnimal Portugal haftet nicht für indirekte Schäden, die aus der Nutzung unserer Dienste entstehen. Die ANF-Therapie ist ergänzend und ersetzt keine konventionelle tierärztliche Behandlung.',
    terms_section5_title: 'Stornierungen und Rückerstattungen',
    terms_section5_text: 'Termine können 24 Stunden im Voraus kostenlos storniert werden. Späte Stornierungen können gebührenpflichtig sein.',
    terms_section6_title: 'Anwendbares Recht',
    terms_section6_text: 'Diese Bedingungen unterliegen dem portugiesischen Recht. Streitigkeiten werden den Gerichten in Portimão, Portugal, vorgelegt.',
    academy_hero_title: 'Ausbildungsakademie',
    academy_hero_subtitle: 'Finden Sie die Ursache jedes Schmerzes, jeder Verletzung und/oder Pathologie und verstehen Sie die Tieranatomie als Einheit. 6 Ausbildungsstufen zur Beherrschung der ANF Animal Therapy®.',
    academy_pillars_title: 'Kernpfeiler',
    academy_pillar1_title: 'Neurologische Funktion',
    academy_pillar1_desc: 'Die Komplexität und Funktionen des Nervensystems, die es dem Tier ermöglichen, sich mit ANF Animal Therapy® selbst zu heilen.',
    academy_pillar2_title: 'Biochemie & Ressourcen',
    academy_pillar2_desc: 'Die Rolle von Zellen, Molekülen, Hormonen und Vitaminen und ihre Wechselwirkungen, die notwendig sind, um die Fusionen und Frequenzen zur Reparatur der Dysfunktionen zu ermöglichen.',
    academy_pillar3_title: 'Neurofrequenz-Medizin',
    academy_pillar3_desc: 'Normalisierung von Entzündungen und neurologischen Dysfunktionen mit dem effektivsten Technologiegerät unter Verwendung revolutionärer Neurofrequenzen, die dem Körper Selbstreparatur ohne Nebenwirkungen oder Medikamente ermöglichen.',
    academy_pillar4_title: 'Klinische Ausbildung & Implementierung',
    academy_pillar4_desc: 'Erlernen Sie neurologische Untersuchungstechniken für herausragende palpatorische und praktische Fähigkeiten im muskuloskelettalen, nervösen, lymphatischen, Verdauungs- und endokrinen System.',
    academy_curriculum: 'Lehrplan',
    academy_edu_title: '6 Ausbildungsstufen',
    academy_edu_subtitle: 'Ein strukturierter Weg zur Beherrschung der ANF Animal Therapy®',
    academy_level1_title: 'Schmerz & Entzündung',
    academy_level1_topics: 'Frequenzen & Methodik,Schmerz & Entzündung,Antioxidantien,Protokolle & Anwendung',
    academy_level2_title: 'Nervensystem',
    academy_level2_topics: 'Nervensystem,Artenvergleich,Behandlungspläne,Protokolle & Anwendung',
    academy_level3_title: 'Lymphsystem',
    academy_level3_topics: 'Lymphzirkulation,Lymph-Biofunktionen inkl. Immunsystem,Lymphuntersuchung,Lymphbehandlung',
    academy_level4_title: 'Kardial / Behandlungsphasen',
    academy_level4_topics: 'Kardiales System,Heilungsphasen,Behandlungsphasen,Protokolle & Anwendung',
    academy_level5_title: 'Leistung & Erholung',
    academy_level5_topics: 'Tierenergie,Organenergie,Zelluläre Energie,Testosteron / Hormone',
    academy_level6_title: 'Praxiskurs',
    academy_level6_topics: 'Untersuchungstechniken,Protokolldesign,Disc-Anwendung,Abschlussbewertung',
    academy_impl_title: 'Erfolgreiche Implementierung',
    academy_impl_subtitle: 'Alles, was Sie für eine erfolgreiche Implementierung der ANF Animal Therapy® benötigen.',
    academy_impl_tools: 'Protokollanwendung,Downloads & Vorlagen,Selbststudium & Quizze,Palpationstraining,Mentoring & Foren,Fälle & Ergebnisse,Prüfung & Zertifikat',
    academy_join_title: 'Wer Kann Teilnehmen?',
    academy_join_subtitle: 'Willkommen bei der ANF Animal Therapy® Ausbildung.',
    academy_join_roles: 'Tierärzte,Pferdephysiotherapeuten,Pferdeosteopathen,Pferdeakupunkteure,Pferdemassagetherapeuten,Pferdechiropraktiker,Pfleger,Tiertrainer',
    cookie_text: 'Diese Website verwendet Cookies, um Ihre Erfahrung zu verbessern. Durch das Weitersurfen stimmen Sie unserer Verwendung von Cookies gemäß den europäischen Vorschriften zu.',
    cookie_accept: 'Akzeptieren',
    cookie_reject: 'Ablehnen',
    cookie_privacy_link: 'Datenschutz',
  },
  fr: {
    nav_home: 'Accueil',
    nav_about: 'À Propos',
    nav_services: 'Services',
    nav_contact: 'Contact',
    nav_facilities: 'Installations',
    nav_academy: 'Académie',
    facilities_title: 'Nos Installations',
    facilities_subtitle: 'Découvrez notre espace privilégié à Porches, Algarve — un environnement d\'excellence pour le bien-être animal.',
    facilities_video_title: 'Vidéo de la Clinique',
    hero_title: 'Clinique & Académie de Thérapie par Fréquence Animale',
    hero_subtitle: 'Technologie ANF révolutionnaire pour le bien-être de votre animal. Traitements non invasifs avec des disques de fréquence thérapeutique.',
    hero_cta_booking: 'Prendre Rendez-vous',
    hero_cta_academy: 'Formation Professionnelle',
    about_title: 'À Propos d\'ANFAnimal Portugal',
    about_subtitle: 'Excellence en thérapie animale avec une technologie de pointe',
    about_description: 'ANFAnimal Portugal est la clinique de référence en thérapie par fréquence pour animaux dans la Péninsule Ibérique. Nous combinons la technologie brevetée ANF avec une équipe de professionnels hautement qualifiés.',
    about_academy_title: 'Académie de Formation',
    about_academy_desc: 'Nous formons des professionnels vétérinaires à la technologie ANF Animal. Cours certifiés avec pratique clinique supervisée.',
    about_academy_button: 'En Savoir Plus',
    about_stat_pain: '+50%',
    about_stat_pain_label: 'Réduction de la Douleur',
    about_stat_discs: '30+',
    about_stat_discs_label: 'Disques Thérapeutiques',
    about_stat_countries: '40+',
    about_stat_countries_label: 'Pays',
    about_stat_years: '10+',
    about_stat_years_label: 'Années de Recherche',
    about_device_title: 'Dispositif Médical ANF',
    about_device_desc: 'Découvrez la technologie brevetée ANF Animal — des disques de fréquence thérapeutique qui révolutionnent le traitement animal.',
    about_device_button: 'Voir le Dispositif Médical',
    popup_back: 'Retour au Site',
    services_title: 'Services & Traitements',
    services_subtitle: 'Thérapies par fréquence non invasives pour votre animal',
    service_pain: 'Gestion de la Douleur',
    service_pain_desc: 'Traitement efficace de la douleur chronique et aiguë avec des disques de fréquence ANF.',
    service_neuro: 'Thérapie Neurologique',
    service_neuro_desc: 'Fréquences spécifiques pour les affections neurologiques, amélioration de la communication nerveuse.',
    service_inflammation: 'Anti-Inflammation',
    service_inflammation_desc: 'Réduction naturelle de l\'inflammation sans médicaments avec des fréquences thérapeutiques.',
    service_recovery: 'Récupération Post-Chirurgicale',
    service_recovery_desc: 'Accélération de la récupération après chirurgie avec des disques favorisant la régénération cellulaire.',
    services_how_title: 'Comment Ça Marche',
    services_step1: 'Évaluation',
    services_step1_desc: 'Examen complet de l\'animal pour identifier les zones de traitement.',
    services_step2: 'Application',
    services_step2_desc: 'Placement stratégique des disques de fréquence ANF.',
    services_step3: 'Résultats',
    services_step3_desc: 'Amélioration progressive avec des séances de suivi.',
    contact_title: 'Contactez-Nous',
    contact_subtitle: 'Prenez rendez-vous ou demandez plus d\'informations',
    contact_name: 'Nom',
    contact_email: 'Email',
    contact_phone: 'Téléphone',
    contact_message: 'Message',
    contact_send: 'Envoyer le Message',
    contact_address: 'Porches, Algarve',
    contact_schedule: 'Horaires',
    contact_schedule_value: 'Lun-Ven: 9h-18h | Sam: 9h-13h',
    footer_rights: 'Tous droits réservés.',
    footer_privacy: 'Politique de Confidentialité',
    footer_terms: 'Conditions Générales',
    footer_tagline: 'Thérapie par Fréquence Animale Premium',
    privacy_last_updated: 'Dernière mise à jour : Mars 2026',
    privacy_section1_title: 'Informations que Nous Collectons',
    privacy_section1_text: 'Nous collectons les informations personnelles que vous nous fournissez volontairement lorsque vous nous contactez, y compris le nom, l\'e-mail, le téléphone et les détails sur votre animal.',
    privacy_section2_title: 'Utilisation des Informations',
    privacy_section2_text: 'Nous utilisons vos informations pour fournir nos services de thérapie ANF Animal, planifier des rendez-vous, communiquer avec vous et améliorer nos services.',
    privacy_section3_title: 'Protection des Données',
    privacy_section3_text: 'Nous mettons en œuvre des mesures de sécurité appropriées pour protéger vos informations personnelles contre tout accès non autorisé, modification ou destruction.',
    privacy_section4_title: 'Cookies',
    privacy_section4_text: 'Notre site web utilise des cookies pour améliorer votre expérience de navigation. Vous pouvez configurer votre navigateur pour refuser les cookies.',
    privacy_section5_title: 'Vos Droits',
    privacy_section5_text: 'En vertu du RGPD, vous avez le droit d\'accéder, rectifier, supprimer et transférer vos données personnelles. Contactez-nous pour exercer ces droits.',
    privacy_section6_title: 'Contact',
    privacy_section6_text: 'Pour toute question relative à la confidentialité, contactez-nous à geral@anfanimalclinicportugal.com, par téléphone au +351 928 281 833, ou visitez notre clinique à R. Porches Velho, 8400-490 Porches.',
    terms_last_updated: 'Dernière mise à jour : Mars 2026',
    terms_section1_title: 'Acceptation des Conditions',
    terms_section1_text: 'En accédant et en utilisant le site web d\'ANFAnimal Portugal, vous acceptez ces conditions générales. Si vous n\'êtes pas d\'accord, n\'utilisez pas le site.',
    terms_section2_title: 'Services',
    terms_section2_text: 'ANFAnimal Portugal propose une thérapie par fréquence non invasive pour les animaux utilisant la technologie ANF. Les résultats peuvent varier et nous ne garantissons pas de résultats spécifiques.',
    terms_section3_title: 'Propriété Intellectuelle',
    terms_section3_text: 'Tout le contenu du site, y compris les textes, images, logos et design, est la propriété d\'ANFAnimal Portugal et est protégé par le droit d\'auteur.',
    terms_section4_title: 'Limitation de Responsabilité',
    terms_section4_text: 'ANFAnimal Portugal n\'est pas responsable des dommages indirects résultant de l\'utilisation de nos services. La thérapie ANF est complémentaire et ne remplace pas le traitement vétérinaire conventionnel.',
    terms_section5_title: 'Annulations et Remboursements',
    terms_section5_text: 'Les rendez-vous peuvent être annulés 24 heures à l\'avance sans frais. Les annulations tardives peuvent être soumises à des frais.',
    terms_section6_title: 'Droit Applicable',
    terms_section6_text: 'Ces conditions sont régies par le droit portugais. Tout litige sera soumis aux tribunaux de Portimão, Portugal.',
    academy_hero_title: 'Académie de Formation',
    academy_hero_subtitle: 'Trouvez la cause profonde de toute douleur, blessure et/ou pathologie et comprenez l\'anatomie animale comme une unité. 6 niveaux éducatifs pour maîtriser l\'ANF Animal Therapy®.',
    academy_pillars_title: 'Piliers Fondamentaux',
    academy_pillar1_title: 'Fonction Neurologique',
    academy_pillar1_desc: 'La complexité et les fonctions du système nerveux qui permettent à l\'animal de s\'auto-guérir grâce à l\'ANF Animal Therapy®.',
    academy_pillar2_title: 'Biochimie & Ressources',
    academy_pillar2_desc: 'Le rôle des cellules, molécules, hormones et vitamines et leurs interactions nécessaires pour faciliter les fusions et fréquences requises pour réparer les dysfonctionnements.',
    academy_pillar3_title: 'Médecine de Neurofréquence',
    academy_pillar3_desc: 'Normaliser l\'inflammation et les dysfonctionnements neurologiques avec le dispositif technologique le plus efficace utilisant des neurofréquences révolutionnaires permettant au corps de s\'auto-réparer sans effets secondaires ni médicaments.',
    academy_pillar4_title: 'Formation Clinique & Implémentation',
    academy_pillar4_desc: 'Apprenez les techniques d\'examen neurologique pour acquérir des compétences palpatoires et pratiques exceptionnelles dans les systèmes musculo-squelettique, nerveux, lymphatique, digestif et endocrinien.',
    academy_curriculum: 'Programme',
    academy_edu_title: '6 Niveaux Éducatifs',
    academy_edu_subtitle: 'Un parcours structuré pour maîtriser l\'ANF Animal Therapy®',
    academy_level1_title: 'Douleur & Inflammation',
    academy_level1_topics: 'Fréquences & Méthodologie,Douleur & Inflammation,Antioxydants,Protocoles & Application',
    academy_level2_title: 'Système Nerveux',
    academy_level2_topics: 'Système Nerveux,Comparaison des Espèces,Plans de Traitement,Protocoles & Application',
    academy_level3_title: 'Système Lymphatique',
    academy_level3_topics: 'Circulation Lymphatique,Fonctions Bio Lymphatiques incl. Système Immunitaire,Examen Lymphatique,Traitement Lymphatique',
    academy_level4_title: 'Cardiaque / Phases de Traitement',
    academy_level4_topics: 'Système Cardiaque,Phases de Guérison,Phases de Traitement,Protocoles & Application',
    academy_level5_title: 'Performance & Récupération',
    academy_level5_topics: 'Énergie Animale,Énergie des Organes,Énergie Cellulaire,Testostérone / Hormones',
    academy_level6_title: 'Cours Pratique',
    academy_level6_topics: 'Techniques d\'Examen,Conception de Protocoles,Application de Disques,Évaluation Finale',
    academy_impl_title: 'Implémentation Réussie',
    academy_impl_subtitle: 'Tout ce dont vous avez besoin pour une implémentation réussie de l\'ANF Animal Therapy®.',
    academy_impl_tools: 'Application de Protocoles,Téléchargements & Modèles,Auto-apprentissage & Quiz,Formation Palpation,Mentorat & Forums,Cas & Résultats,Examen & Certificat',
    academy_join_title: 'Qui Peut Participer ?',
    academy_join_subtitle: 'Bienvenue dans l\'Éducation ANF Animal Therapy®.',
    academy_join_roles: 'Vétérinaires,Kinésithérapeutes Équins,Ostéopathes Équins,Acupuncteurs Équins,Massothérapeutes Équins,Chiropracteurs Équins,Soigneurs,Entraîneurs Animaux',
    cookie_text: 'Ce site utilise des cookies pour améliorer votre expérience. En continuant à naviguer, vous acceptez notre utilisation des cookies conformément à la réglementation européenne.',
    cookie_accept: 'Accepter',
    cookie_reject: 'Refuser',
    cookie_privacy_link: 'Politique de Confidentialité',
  },
  it: {
    nav_home: 'Home',
    nav_about: 'Chi Siamo',
    nav_services: 'Servizi',
    nav_contact: 'Contatti',
    nav_facilities: 'Strutture',
    nav_academy: 'Accademia',
    facilities_title: 'Le Nostre Strutture',
    facilities_subtitle: 'Scopri il nostro spazio privilegiato a Porches, Algarve — un ambiente di eccellenza per il benessere animale.',
    facilities_video_title: 'Video della Clinica',
    hero_title: 'Clinica & Accademia di Terapia a Frequenza Animale',
    hero_subtitle: 'Tecnologia ANF rivoluzionaria per il benessere del tuo animale. Trattamenti non invasivi con dischi a frequenza terapeutica.',
    hero_cta_booking: 'Prenota Appuntamento',
    hero_cta_academy: 'Formazione Professionale',
    about_title: 'Chi è ANFAnimal Portugal',
    about_subtitle: 'Eccellenza nella terapia animale con tecnologia all\'avanguardia',
    about_description: 'ANFAnimal Portugal è la clinica di riferimento nella terapia a frequenza per animali nella Penisola Iberica. Combiniamo la tecnologia brevettata ANF con professionisti altamente qualificati.',
    about_academy_title: 'Accademia di Formazione',
    about_academy_desc: 'Formiamo professionisti veterinari nella tecnologia ANF Animal. Corsi certificati con pratica clinica supervisionata.',
    about_academy_button: 'Scopri di Più',
    about_stat_pain: '+50%',
    about_stat_pain_label: 'Riduzione del Dolore',
    about_stat_discs: '30+',
    about_stat_discs_label: 'Dischi Terapeutici',
    about_stat_countries: '40+',
    about_stat_countries_label: 'Paesi',
    about_stat_years: '10+',
    about_stat_years_label: 'Anni di Ricerca',
    about_device_title: 'Dispositivo Medico ANF',
    about_device_desc: 'Scopri la tecnologia brevettata ANF Animal — dischi a frequenza terapeutica che rivoluzionano il trattamento animale.',
    about_device_button: 'Vedi Dispositivo Medico',
    popup_back: 'Torna al Sito',
    services_title: 'Servizi & Trattamenti',
    services_subtitle: 'Terapie a frequenza non invasive per il tuo animale',
    service_pain: 'Gestione del Dolore',
    service_pain_desc: 'Trattamento efficace del dolore cronico e acuto con dischi a frequenza ANF.',
    service_neuro: 'Terapia Neurologica',
    service_neuro_desc: 'Frequenze specifiche per condizioni neurologiche, miglioramento della comunicazione nervosa.',
    service_inflammation: 'Anti-Infiammazione',
    service_inflammation_desc: 'Riduzione naturale dell\'infiammazione senza farmaci con frequenze terapeutiche.',
    service_recovery: 'Recupero Post-Chirurgico',
    service_recovery_desc: 'Accelerazione del recupero dopo interventi chirurgici con dischi che promuovono la rigenerazione cellulare.',
    services_how_title: 'Come Funziona',
    services_step1: 'Valutazione',
    services_step1_desc: 'Esame completo dell\'animale per identificare le aree di trattamento.',
    services_step2: 'Applicazione',
    services_step2_desc: 'Posizionamento strategico dei dischi a frequenza ANF.',
    services_step3: 'Risultati',
    services_step3_desc: 'Miglioramento progressivo con sessioni di follow-up.',
    contact_title: 'Contattaci',
    contact_subtitle: 'Prenota il tuo appuntamento o richiedi informazioni',
    contact_name: 'Nome',
    contact_email: 'Email',
    contact_phone: 'Telefono',
    contact_message: 'Messaggio',
    contact_send: 'Invia Messaggio',
    contact_address: 'Porches, Algarve',
    contact_schedule: 'Orari',
    contact_schedule_value: 'Lun-Ven: 9-18 | Sab: 9-13',
    footer_rights: 'Tutti i diritti riservati.',
    footer_privacy: 'Privacy Policy',
    footer_terms: 'Termini e Condizioni',
    footer_tagline: 'Terapia a Frequenza Animale Premium',
    privacy_last_updated: 'Ultimo aggiornamento: Marzo 2026',
    privacy_section1_title: 'Informazioni che Raccogliamo',
    privacy_section1_text: 'Raccogliamo informazioni personali che ci fornite volontariamente quando ci contattate, inclusi nome, email, telefono e dettagli sul vostro animale.',
    privacy_section2_title: 'Utilizzo delle Informazioni',
    privacy_section2_text: 'Utilizziamo le vostre informazioni per fornire i nostri servizi di terapia ANF Animal, programmare appuntamenti, comunicare con voi e migliorare i nostri servizi.',
    privacy_section3_title: 'Protezione dei Dati',
    privacy_section3_text: 'Implementiamo misure di sicurezza adeguate per proteggere le vostre informazioni personali da accesso non autorizzato, alterazione o distruzione.',
    privacy_section4_title: 'Cookie',
    privacy_section4_text: 'Il nostro sito web utilizza cookie per migliorare la vostra esperienza di navigazione. Potete configurare il browser per rifiutare i cookie.',
    privacy_section5_title: 'I Vostri Diritti',
    privacy_section5_text: 'In base al GDPR, avete il diritto di accedere, rettificare, cancellare e trasferire i vostri dati personali. Contattateci per esercitare questi diritti.',
    privacy_section6_title: 'Contatto',
    privacy_section6_text: 'Per domande sulla privacy, contattateci a geral@anfanimalclinicportugal.com, telefonicamente al +351 928 281 833, o visitate la nostra clinica a R. Porches Velho, 8400-490 Porches.',
    terms_last_updated: 'Ultimo aggiornamento: Marzo 2026',
    terms_section1_title: 'Accettazione dei Termini',
    terms_section1_text: 'Accedendo e utilizzando il sito web di ANFAnimal Portugal, accettate questi termini e condizioni. Se non siete d\'accordo, non utilizzate il sito.',
    terms_section2_title: 'Servizi',
    terms_section2_text: 'ANFAnimal Portugal offre terapia a frequenza non invasiva per animali utilizzando la tecnologia ANF. I risultati possono variare e non garantiamo risultati specifici.',
    terms_section3_title: 'Proprietà Intellettuale',
    terms_section3_text: 'Tutto il contenuto del sito, inclusi testi, immagini, loghi e design, è proprietà di ANFAnimal Portugal ed è protetto da copyright.',
    terms_section4_title: 'Limitazione di Responsabilità',
    terms_section4_text: 'ANFAnimal Portugal non è responsabile per danni indiretti derivanti dall\'uso dei nostri servizi. La terapia ANF è complementare e non sostituisce il trattamento veterinario convenzionale.',
    terms_section5_title: 'Cancellazioni e Rimborsi',
    terms_section5_text: 'Gli appuntamenti possono essere cancellati con 24 ore di anticipo senza costi. Le cancellazioni tardive possono essere soggette a un addebito.',
    terms_section6_title: 'Legge Applicabile',
    terms_section6_text: 'Questi termini sono regolati dalla legge portoghese. Qualsiasi controversia sarà sottoposta ai tribunali di Portimão, Portogallo.',
    academy_hero_title: 'Accademia di Formazione',
    academy_hero_subtitle: 'Trova la causa principale di qualsiasi dolore, infortunio e/o patologia e comprendi l\'anatomia animale come un\'unità. 6 livelli educativi per padroneggiare l\'ANF Animal Therapy®.',
    academy_pillars_title: 'Pilastri Fondamentali',
    academy_pillar1_title: 'Funzione Neurologica',
    academy_pillar1_desc: 'La complessità e le funzioni del sistema nervoso che permettono all\'animale di auto-guarirsi con l\'uso dell\'ANF Animal Therapy®.',
    academy_pillar2_title: 'Biochimica e Risorse',
    academy_pillar2_desc: 'Il ruolo di cellule, molecole, ormoni e vitamine e le loro interazioni necessarie per facilitare le fusioni e frequenze richieste per riparare le disfunzioni.',
    academy_pillar3_title: 'Medicina di Neurofrequenza',
    academy_pillar3_desc: 'Normalizzare l\'infiammazione e la disfunzione neurologica con il dispositivo tecnologico più efficace utilizzando neurofrequenze rivoluzionarie che permettono al corpo di auto-ripararsi senza effetti collaterali o farmaci.',
    academy_pillar4_title: 'Formazione Clinica & Implementazione',
    academy_pillar4_desc: 'Impara le tecniche di esame neurologico per acquisire eccezionali competenze palpatorie e pratiche nei sistemi muscoloscheletrico, nervoso, linfatico, digestivo ed endocrino.',
    academy_curriculum: 'Programma',
    academy_edu_title: '6 Livelli Educativi',
    academy_edu_subtitle: 'Un percorso strutturato per padroneggiare l\'ANF Animal Therapy®',
    academy_level1_title: 'Dolore & Infiammazione',
    academy_level1_topics: 'Frequenze & Metodologia,Dolore & Infiammazione,Antiossidanti,Protocolli & Applicazione',
    academy_level2_title: 'Sistema Nervoso',
    academy_level2_topics: 'Sistema Nervoso,Confronto tra Specie,Piani di Trattamento,Protocolli & Applicazione',
    academy_level3_title: 'Sistema Linfatico',
    academy_level3_topics: 'Circolazione Linfatica,Funzioni Bio Linfatiche incl. Sistema Immunitario,Esame Linfatico,Trattamento Linfatico',
    academy_level4_title: 'Cardiaco / Fasi di Trattamento',
    academy_level4_topics: 'Sistema Cardiaco,Fasi di Guarigione,Fasi di Trattamento,Protocolli & Applicazione',
    academy_level5_title: 'Prestazione & Recupero',
    academy_level5_topics: 'Energia Animale,Energia degli Organi,Energia Cellulare,Testosterone / Ormoni',
    academy_level6_title: 'Corso Pratico',
    academy_level6_topics: 'Tecniche di Esame,Progettazione di Protocolli,Applicazione Dischi,Valutazione Finale',
    academy_impl_title: 'Implementazione di Successo',
    academy_impl_subtitle: 'Tutto ciò di cui hai bisogno per un\'implementazione di successo dell\'ANF Animal Therapy®.',
    academy_impl_tools: 'Applicazione Protocolli,Download & Modelli,Auto-studio & Quiz,Formazione Palpazione,Mentoring & Forum,Casi & Risultati,Esame & Certificato',
    academy_join_title: 'Chi Può Partecipare?',
    academy_join_subtitle: 'Benvenuti nell\'Educazione ANF Animal Therapy®.',
    academy_join_roles: 'Veterinari,Fisioterapisti Equini,Osteopati Equini,Agopunturisti Equini,Massoterapisti Equini,Chiropratici Equini,Stallieri,Addestratori di Animali',
    cookie_text: 'Questo sito utilizza i cookie per migliorare la tua esperienza. Continuando a navigare, accetti il nostro utilizzo dei cookie in conformità con la normativa europea.',
    cookie_accept: 'Accetta',
    cookie_reject: 'Rifiuta',
    cookie_privacy_link: 'Privacy Policy',
  },
  no: {
    nav_home: 'Hjem',
    nav_about: 'Om Oss',
    nav_services: 'Tjenester',
    nav_contact: 'Kontakt',
    nav_facilities: 'Fasiliteter',
    nav_academy: 'Akademi',
    facilities_title: 'Våre Fasiliteter',
    facilities_subtitle: 'Oppdag vårt privilegerte rom i Porches, Algarve — et miljø av dyktighet for dyrevelferd.',
    facilities_video_title: 'Klinikkvideo',
    hero_title: 'Dyrefrekvensterapi Klinikk & Akademi',
    hero_subtitle: 'Revolusjonerende ANF-teknologi for dyrets velvære. Ikke-invasive behandlinger med terapeutiske frekvensskiver.',
    hero_cta_booking: 'Bestill Time',
    hero_cta_academy: 'Profesjonell Opplæring',
    about_title: 'Om ANFAnimal Portugal',
    about_subtitle: 'Fremragende dyreterapie med banebrytende teknologi',
    about_description: 'ANFAnimal Portugal er den ledende frekvensterapi-klinikken for dyr på den iberiske halvøy. Vi kombinerer patentert ANF-teknologi med høyt kvalifiserte fagfolk.',
    about_academy_title: 'Opplæringsakademi',
    about_academy_desc: 'Vi utdanner veterinære fagfolk i ANF Animal-teknologi. Sertifiserte kurs med veiledet klinisk praksis.',
    about_academy_button: 'Les Mer',
    about_stat_pain: '+50%',
    about_stat_pain_label: 'Smertereduksjon',
    about_stat_discs: '30+',
    about_stat_discs_label: 'Terapeutiske Skiver',
    about_stat_countries: '40+',
    about_stat_countries_label: 'Land',
    about_stat_years: '10+',
    about_stat_years_label: 'År med Forskning',
    about_device_title: 'ANF Medisinsk Enhet',
    about_device_desc: 'Oppdag den patenterte ANF Animal-teknologien — terapeutiske frekvensplater som revolusjonerer dyrebehandling.',
    about_device_button: 'Se Medisinsk Enhet',
    popup_back: 'Tilbake til Nettstedet',
    services_title: 'Tjenester & Behandlinger',
    services_subtitle: 'Ikke-invasive frekvensterapier for ditt dyr',
    service_pain: 'Smertebehandling',
    service_pain_desc: 'Effektiv behandling av kronisk og akutt smerte med ANF-frekvensskiver.',
    service_neuro: 'Nevrologisk Terapi',
    service_neuro_desc: 'Spesifikke frekvenser for nevrologiske tilstander, forbedring av nervekommunikasjon.',
    service_inflammation: 'Betennelsesdempende',
    service_inflammation_desc: 'Naturlig betennelsesreduksjon uten medisiner med terapeutiske frekvenser.',
    service_recovery: 'Postoperativ Gjenoppretting',
    service_recovery_desc: 'Akselerering av gjenoppretting etter operasjoner med skiver som fremmer celleregenerering.',
    services_how_title: 'Hvordan Det Fungerer',
    services_step1: 'Vurdering',
    services_step1_desc: 'Fullstendig undersøkelse av dyret for å identifisere behandlingsområder.',
    services_step2: 'Anvendelse',
    services_step2_desc: 'Strategisk plassering av ANF-frekvensskiver.',
    services_step3: 'Resultater',
    services_step3_desc: 'Progressiv forbedring med oppfølgingsbehandlinger.',
    contact_title: 'Kontakt Oss',
    contact_subtitle: 'Bestill din time eller be om mer informasjon',
    contact_name: 'Navn',
    contact_email: 'E-post',
    contact_phone: 'Telefon',
    contact_message: 'Melding',
    contact_send: 'Send Melding',
    contact_address: 'Porches, Algarve',
    contact_schedule: 'Åpningstider',
    contact_schedule_value: 'Man-Fre: 9-18 | Lør: 9-13',
    footer_rights: 'Alle rettigheter reservert.',
    footer_privacy: 'Personvern',
    footer_terms: 'Vilkår og Betingelser',
    footer_tagline: 'Premium Dyrefrekvensterapi',
    privacy_last_updated: 'Sist oppdatert: Mars 2026',
    privacy_section1_title: 'Informasjon Vi Samler Inn',
    privacy_section1_text: 'Vi samler inn personlig informasjon som du frivillig gir oss når du kontakter oss, inkludert navn, e-post, telefonnummer og detaljer om kjæledyret ditt.',
    privacy_section2_title: 'Bruk av Informasjon',
    privacy_section2_text: 'Vi bruker informasjonen din til å levere våre ANF Animal terapitjenester, planlegge avtaler, kommunisere med deg og forbedre tjenestene våre.',
    privacy_section3_title: 'Databeskyttelse',
    privacy_section3_text: 'Vi implementerer passende sikkerhetstiltak for å beskytte din personlige informasjon mot uautorisert tilgang, endring eller ødeleggelse.',
    privacy_section4_title: 'Informasjonskapsler',
    privacy_section4_text: 'Nettstedet vårt bruker informasjonskapsler for å forbedre nettleseropplevelsen din. Du kan konfigurere nettleseren til å avvise informasjonskapsler.',
    privacy_section5_title: 'Dine Rettigheter',
    privacy_section5_text: 'Under GDPR har du rett til å få tilgang til, rette, slette og overføre dine personopplysninger. Kontakt oss for å utøve disse rettighetene.',
    privacy_section6_title: 'Kontakt',
    privacy_section6_text: 'For personvernspørsmål, kontakt oss på geral@anfanimalclinicportugal.com, på telefon +351 928 281 833, eller besøk klinikken vår på R. Porches Velho, 8400-490 Porches.',
    terms_last_updated: 'Sist oppdatert: Mars 2026',
    terms_section1_title: 'Aksept av Vilkår',
    terms_section1_text: 'Ved å besøke og bruke ANFAnimal Portugals nettsted, godtar du disse vilkårene og betingelsene. Hvis du ikke er enig, bruk ikke nettstedet.',
    terms_section2_title: 'Tjenester',
    terms_section2_text: 'ANFAnimal Portugal tilbyr ikke-invasiv frekvensterapi for dyr ved bruk av ANF-teknologi. Resultatene kan variere og vi garanterer ikke spesifikke resultater.',
    terms_section3_title: 'Immaterielle Rettigheter',
    terms_section3_text: 'Alt innhold på nettstedet, inkludert tekster, bilder, logoer og design, tilhører ANFAnimal Portugal og er beskyttet av opphavsrett.',
    terms_section4_title: 'Ansvarsbegrensning',
    terms_section4_text: 'ANFAnimal Portugal er ikke ansvarlig for indirekte skader som følge av bruk av våre tjenester. ANF-terapi er komplementær og erstatter ikke konvensjonell veterinærbehandling.',
    terms_section5_title: 'Kanselleringer og Refusjoner',
    terms_section5_text: 'Avtaler kan kanselleres 24 timer i forveien uten kostnad. Sene kanselleringer kan medføre et gebyr.',
    terms_section6_title: 'Gjeldende Lov',
    terms_section6_text: 'Disse vilkårene er underlagt portugisisk lov. Enhver tvist skal forelegges domstolene i Portimão, Portugal.',
    academy_hero_title: 'Opplæringsakademi',
    academy_hero_subtitle: 'Finn rotårsaken til enhver smerte, skade og/eller patologi og forstå dyreanatomi som en enhet. 6 utdanningsnivåer for å mestre ANF Animal Therapy®.',
    academy_pillars_title: 'Kjernepilarer',
    academy_pillar1_title: 'Nevrologisk Funksjon',
    academy_pillar1_desc: 'Kompleksiteten og funksjonene til nervesystemet som gjør at dyret kan selvhele med bruk av ANF Animal Therapy®.',
    academy_pillar2_title: 'Biokjemi & Ressurser',
    academy_pillar2_desc: 'Rollen til celler, molekyler, hormoner og vitaminer og deres interaksjoner som er nødvendige for å tilrettelegge fusjonene og frekvensene som kreves for å reparere dysfunksjonene.',
    academy_pillar3_title: 'Nevrofrekvens Medisin',
    academy_pillar3_desc: 'Normaliser betennelse og nevrologisk dysfunksjon med det mest effektive teknologiske apparatet med revolusjonerende nevrofrekvenser som gjør at kroppen kan selvreparere uten bivirkninger eller medisiner.',
    academy_pillar4_title: 'Klinisk Opplæring & Implementering',
    academy_pillar4_desc: 'Lær nevrologiske undersøkelsesteknikker for fremragende palpatoriske og praktiske ferdigheter i muskel-skjelett-, nerve-, lymfe-, fordøyelses- og endokrine systemer.',
    academy_curriculum: 'Læreplan',
    academy_edu_title: '6 Utdanningsnivåer',
    academy_edu_subtitle: 'En strukturert vei til å mestre ANF Animal Therapy®',
    academy_level1_title: 'Smerte & Betennelse',
    academy_level1_topics: 'Frekvenser & Metodikk,Smerte & Betennelse,Antioksidanter,Protokoller & Anvendelse',
    academy_level2_title: 'Nervesystemet',
    academy_level2_topics: 'Nervesystemet,Artssammenligning,Behandlingsplaner,Protokoller & Anvendelse',
    academy_level3_title: 'Lymfesystemet',
    academy_level3_topics: 'Lymfesirkulasjon,Lymfe Biofunksjoner inkl. Immunsystem,Lymfeundersøkelse,Lymfebehandling',
    academy_level4_title: 'Hjerte / Behandlingsfaser',
    academy_level4_topics: 'Hjertesystem,Helingsfaser,Behandlingsfaser,Protokoller & Anvendelse',
    academy_level5_title: 'Ytelse & Restitusjon',
    academy_level5_topics: 'Dyreenergi,Organenergi,Cellulær Energi,Testosteron / Hormoner',
    academy_level6_title: 'Praktisk Kurs',
    academy_level6_topics: 'Undersøkelsesteknikker,Protokolldesign,Disc-anvendelse,Sluttbedømmelse',
    academy_impl_title: 'Vellykket Implementering',
    academy_impl_subtitle: 'Alt du trenger for vellykket implementering av ANF Animal Therapy®.',
    academy_impl_tools: 'Protokollanvendelse,Nedlastinger & Maler,Selvstudium & Quizzer,Palpasjonstrening,Veiledning & Forum,Saker & Resultater,Eksamen & Sertifikat',
    academy_join_title: 'Hvem Kan Delta?',
    academy_join_subtitle: 'Velkommen til ANF Animal Therapy® Utdanning.',
    academy_join_roles: 'Veterinærer,Hestefysioterapeuter,Hesteosteopater,Hesteakupunktører,Hestemassasjeterapeuter,Hestekiropraktorer,Stallpersonell,Dyretrenere',
    cookie_text: 'Denne nettsiden bruker informasjonskapsler for å forbedre opplevelsen din. Ved å fortsette å surfe godtar du vår bruk av informasjonskapsler i samsvar med europeisk regelverk.',
    cookie_accept: 'Godta',
    cookie_reject: 'Avvis',
    cookie_privacy_link: 'Personvern',
  },
  da: {
    nav_home: 'Hjem',
    nav_about: 'Om Os',
    nav_services: 'Tjenester',
    nav_contact: 'Kontakt',
    nav_facilities: 'Faciliteter',
    nav_academy: 'Akademi',
    facilities_title: 'Vores Faciliteter',
    facilities_subtitle: 'Opdag vores privilegerede rum i Porches, Algarve — et miljø af ekspertise for dyrevelfærd.',
    facilities_video_title: 'Klinikvideo',
    hero_title: 'Dyrefrekvensterapi Klinik & Akademi',
    hero_subtitle: 'Revolutionerende ANF-teknologi til dit dyrs velbefindende. Ikke-invasive behandlinger med terapeutiske frekvensskiver.',
    hero_cta_booking: 'Book Tid',
    hero_cta_academy: 'Professionel Uddannelse',
    about_title: 'Om ANFAnimal Portugal',
    about_subtitle: 'Excellence i dyreterapie med banebrydende teknologi',
    about_description: 'ANFAnimal Portugal er den førende frekvensterapi-klinik for dyr på den iberiske halvø. Vi kombinerer patenteret ANF-teknologi med højt kvalificerede fagfolk.',
    about_academy_title: 'Uddannelsesakademi',
    about_academy_desc: 'Vi uddanner veterinære fagfolk i ANF Animal teknologi. Certificerede kurser med superviseret klinisk praksis.',
    about_academy_button: 'Læs Mere',
    about_stat_pain: '+50%',
    about_stat_pain_label: 'Smertereduktion',
    about_stat_discs: '30+',
    about_stat_discs_label: 'Terapeutiske Skiver',
    about_stat_countries: '40+',
    about_stat_countries_label: 'Lande',
    about_stat_years: '10+',
    about_stat_years_label: 'Års Forskning',
    about_device_title: 'ANF Medicinsk Enhed',
    about_device_desc: 'Opdag den patenterede ANF Animal-teknologi — terapeutiske frekvensplader der revolutionerer dyrebehandling.',
    about_device_button: 'Se Medicinsk Enhed',
    popup_back: 'Tilbage til Hjemmesiden',
    services_title: 'Tjenester & Behandlinger',
    services_subtitle: 'Ikke-invasive frekvensterapier til dit dyr',
    service_pain: 'Smertebehandling',
    service_pain_desc: 'Effektiv behandling af kronisk og akut smerte med ANF-frekvensskiver.',
    service_neuro: 'Neurologisk Terapi',
    service_neuro_desc: 'Specifikke frekvenser til neurologiske tilstande, forbedring af nervekommunikation.',
    service_inflammation: 'Antiinflammatorisk',
    service_inflammation_desc: 'Naturlig inflammationsreduktion uden medicin med terapeutiske frekvenser.',
    service_recovery: 'Postoperativ Genopretning',
    service_recovery_desc: 'Acceleration af genopretning efter operationer med skiver der fremmer celleregenerering.',
    services_how_title: 'Sådan Fungerer Det',
    services_step1: 'Vurdering',
    services_step1_desc: 'Komplet undersøgelse af dyret for at identificere behandlingsområder.',
    services_step2: 'Anvendelse',
    services_step2_desc: 'Strategisk placering af ANF-frekvensskiver.',
    services_step3: 'Resultater',
    services_step3_desc: 'Progressiv forbedring med opfølgningsbehandlinger.',
    contact_title: 'Kontakt Os',
    contact_subtitle: 'Book din tid eller anmod om mere information',
    contact_name: 'Navn',
    contact_email: 'E-mail',
    contact_phone: 'Telefon',
    contact_message: 'Besked',
    contact_send: 'Send Besked',
    contact_address: 'Porches, Algarve',
    contact_schedule: 'Åbningstider',
    contact_schedule_value: 'Man-Fre: 9-18 | Lør: 9-13',
    footer_rights: 'Alle rettigheder forbeholdes.',
    footer_privacy: 'Privatlivspolitik',
    footer_terms: 'Vilkår og Betingelser',
    footer_tagline: 'Premium Dyrefrekvensterapi',
    privacy_last_updated: 'Sidst opdateret: Marts 2026',
    privacy_section1_title: 'Information Vi Indsamler',
    privacy_section1_text: 'Vi indsamler personlige oplysninger, som du frivilligt giver os, når du kontakter os, herunder navn, e-mail, telefonnummer og oplysninger om dit kæledyr.',
    privacy_section2_title: 'Brug af Information',
    privacy_section2_text: 'Vi bruger dine oplysninger til at levere vores ANF Animal terapitjenester, planlægge aftaler, kommunikere med dig og forbedre vores tjenester.',
    privacy_section3_title: 'Databeskyttelse',
    privacy_section3_text: 'Vi implementerer passende sikkerhedsforanstaltninger for at beskytte dine personlige oplysninger mod uautoriseret adgang, ændring eller ødelæggelse.',
    privacy_section4_title: 'Cookies',
    privacy_section4_text: 'Vores hjemmeside bruger cookies for at forbedre din browsingoplevelse. Du kan konfigurere din browser til at afvise cookies.',
    privacy_section5_title: 'Dine Rettigheder',
    privacy_section5_text: 'Under GDPR har du ret til at få adgang til, rette, slette og overføre dine personoplysninger. Kontakt os for at udøve disse rettigheder.',
    privacy_section6_title: 'Kontakt',
    privacy_section6_text: 'For spørgsmål om privatliv, kontakt os på geral@anfanimalclinicportugal.com, på telefon +351 928 281 833, eller besøg vores klinik på R. Porches Velho, 8400-490 Porches.',
    terms_last_updated: 'Sidst opdateret: Marts 2026',
    terms_section1_title: 'Accept af Vilkår',
    terms_section1_text: 'Ved at besøge og bruge ANFAnimal Portugals hjemmeside accepterer du disse vilkår og betingelser. Hvis du ikke er enig, brug ikke hjemmesiden.',
    terms_section2_title: 'Tjenester',
    terms_section2_text: 'ANFAnimal Portugal tilbyder ikke-invasiv frekvensterapi til dyr ved hjælp af ANF-teknologi. Resultaterne kan variere, og vi garanterer ikke specifikke resultater.',
    terms_section3_title: 'Immaterielle Rettigheder',
    terms_section3_text: 'Alt indhold på hjemmesiden, herunder tekster, billeder, logoer og design, tilhører ANFAnimal Portugal og er beskyttet af ophavsret.',
    terms_section4_title: 'Ansvarsbegrænsning',
    terms_section4_text: 'ANFAnimal Portugal er ikke ansvarlig for indirekte skader som følge af brugen af vores tjenester. ANF-terapi er komplementær og erstatter ikke konventionel veterinærbehandling.',
    terms_section5_title: 'Aflysninger og Refusioner',
    terms_section5_text: 'Aftaler kan aflyses 24 timer i forvejen uden omkostninger. Sene aflysninger kan være underlagt et gebyr.',
    terms_section6_title: 'Gældende Lov',
    terms_section6_text: 'Disse vilkår er underlagt portugisisk lov. Enhver tvist skal indbringes for domstolene i Portimão, Portugal.',
    academy_hero_title: 'Uddannelsesakademi',
    academy_hero_subtitle: 'Find årsagen til enhver smerte, skade og/eller patologi og forstå dyreanatomi som en enhed. 6 uddannelsesniveauer til at mestre ANF Animal Therapy®.',
    academy_pillars_title: 'Kernepiller',
    academy_pillar1_title: 'Neurologisk Funktion',
    academy_pillar1_desc: 'Kompleksiteten og funktionerne i nervesystemet, der gør det muligt for dyret at selvhele med brug af ANF Animal Therapy®.',
    academy_pillar2_title: 'Biokemi & Ressourcer',
    academy_pillar2_desc: 'Rollen af celler, molekyler, hormoner og vitaminer og deres interaktioner, der er nødvendige for at facilitere fusionerne og frekvenserne, der kræves for at reparere dysfunktionerne.',
    academy_pillar3_title: 'Neurofrekvens Medicin',
    academy_pillar3_desc: 'Normaliser betændelse og neurologisk dysfunktion med det mest effektive teknologiske apparat med revolutionerende neurofrekvenser, der gør det muligt for kroppen at selvreparere uden bivirkninger eller medicin.',
    academy_pillar4_title: 'Klinisk Uddannelse & Implementering',
    academy_pillar4_desc: 'Lær neurologiske undersøgelsesteknikker for fremragende palpatoriske og praktiske færdigheder i muskel-skelet-, nerve-, lymfe-, fordøjelses- og endokrine systemer.',
    academy_curriculum: 'Læseplan',
    academy_edu_title: '6 Uddannelsesniveauer',
    academy_edu_subtitle: 'En struktureret vej til at mestre ANF Animal Therapy®',
    academy_level1_title: 'Smerte & Betændelse',
    academy_level1_topics: 'Frekvenser & Metodik,Smerte & Betændelse,Antioxidanter,Protokoller & Anvendelse',
    academy_level2_title: 'Nervesystemet',
    academy_level2_topics: 'Nervesystemet,Artssammenligning,Behandlingsplaner,Protokoller & Anvendelse',
    academy_level3_title: 'Lymfesystemet',
    academy_level3_topics: 'Lymfecirkulation,Lymfe Biofunktioner inkl. Immunsystem,Lymfeundersøgelse,Lymfebehandling',
    academy_level4_title: 'Hjerte / Behandlingsfaser',
    academy_level4_topics: 'Hjertesystem,Helbredsfaser,Behandlingsfaser,Protokoller & Anvendelse',
    academy_level5_title: 'Ydeevne & Restitution',
    academy_level5_topics: 'Dyreenergi,Organenergi,Cellulær Energi,Testosteron / Hormoner',
    academy_level6_title: 'Praktisk Kursus',
    academy_level6_topics: 'Undersøgelsesteknikker,Protokoldesign,Disc-anvendelse,Slutbedømmelse',
    academy_impl_title: 'Succesfuld Implementering',
    academy_impl_subtitle: 'Alt hvad du har brug for til succesfuld implementering af ANF Animal Therapy®.',
    academy_impl_tools: 'Protokolanvendelse,Downloads & Skabeloner,Selvstudium & Quizzer,Palpationstræning,Mentoring & Fora,Sager & Resultater,Eksamen & Certifikat',
    academy_join_title: 'Hvem Kan Deltage?',
    academy_join_subtitle: 'Velkommen til ANF Animal Therapy® Uddannelse.',
    academy_join_roles: 'Dyrlæger,Hestefysioterapeuter,Hesteosteopater,Hesteakupunktører,Hestemassageterapeuter,Hestekiropratkorer,Plejere,Dyretrænere',
    cookie_text: 'Denne hjemmeside bruger cookies for at forbedre din oplevelse. Ved at fortsætte med at browse accepterer du vores brug af cookies i overensstemmelse med europæisk lovgivning.',
    cookie_accept: 'Acceptér',
    cookie_reject: 'Afvis',
    cookie_privacy_link: 'Privatlivspolitik',
  },
  sv: {
    nav_home: 'Hem',
    nav_about: 'Om Oss',
    nav_services: 'Tjänster',
    nav_contact: 'Kontakt',
    nav_facilities: 'Anläggningar',
    nav_academy: 'Akademi',
    facilities_title: 'Våra Anläggningar',
    facilities_subtitle: 'Upptäck vårt privilegierade utrymme i Porches, Algarve — en miljö av excellens för djurvälfärd.',
    facilities_video_title: 'Klinikvideo',
    hero_title: 'Djurfrekvensterapi Klinik & Akademi',
    hero_subtitle: 'Revolutionerande ANF-teknologi för ditt djurs välbefinnande. Icke-invasiva behandlingar med terapeutiska frekvensskivor.',
    hero_cta_booking: 'Boka Tid',
    hero_cta_academy: 'Professionell Utbildning',
    about_title: 'Om ANFAnimal Portugal',
    about_subtitle: 'Excellens inom djurterapie med banbrytande teknologi',
    about_description: 'ANFAnimal Portugal är den ledande frekvensterapi-kliniken för djur på den iberiska halvön. Vi kombinerar patenterad ANF-teknologi med högt kvalificerade yrkesverksamma.',
    about_academy_title: 'Utbildningsakademi',
    about_academy_desc: 'Vi utbildar veterinära yrkesverksamma i ANF Animal-teknologi. Certifierade kurser med handledd klinisk praktik.',
    about_academy_button: 'Läs Mer',
    about_stat_pain: '+50%',
    about_stat_pain_label: 'Smärtreducering',
    about_stat_discs: '30+',
    about_stat_discs_label: 'Terapeutiska Skivor',
    about_stat_countries: '40+',
    about_stat_countries_label: 'Länder',
    about_stat_years: '10+',
    about_stat_years_label: 'Års Forskning',
    about_device_title: 'ANF Medicinsk Enhet',
    about_device_desc: 'Upptäck den patenterade ANF Animal-teknologin — terapeutiska frekvenskivor som revolutionerar djurbehandling.',
    about_device_button: 'Se Medicinsk Enhet',
    popup_back: 'Tillbaka till Webbplatsen',
    services_title: 'Tjänster & Behandlingar',
    services_subtitle: 'Icke-invasiva frekvensterapier för ditt djur',
    service_pain: 'Smärthantering',
    service_pain_desc: 'Effektiv behandling av kronisk och akut smärta med ANF-frekvensskivor.',
    service_neuro: 'Neurologisk Terapi',
    service_neuro_desc: 'Specifika frekvenser för neurologiska tillstånd, förbättring av nervkommunikation.',
    service_inflammation: 'Antiinflammatorisk',
    service_inflammation_desc: 'Naturlig inflammationsminskning utan mediciner med terapeutiska frekvenser.',
    service_recovery: 'Postoperativ Återhämtning',
    service_recovery_desc: 'Acceleration av återhämtning efter operationer med skivor som främjar cellregenerering.',
    services_how_title: 'Hur Det Fungerar',
    services_step1: 'Bedömning',
    services_step1_desc: 'Fullständig undersökning av djuret för att identifiera behandlingsområden.',
    services_step2: 'Tillämpning',
    services_step2_desc: 'Strategisk placering av ANF-frekvensskivor.',
    services_step3: 'Resultat',
    services_step3_desc: 'Progressiv förbättring med uppföljningsbehandlingar.',
    contact_title: 'Kontakta Oss',
    contact_subtitle: 'Boka din tid eller begär mer information',
    contact_name: 'Namn',
    contact_email: 'E-post',
    contact_phone: 'Telefon',
    contact_message: 'Meddelande',
    contact_send: 'Skicka Meddelande',
    contact_address: 'Porches, Algarve',
    contact_schedule: 'Öppettider',
    contact_schedule_value: 'Mån-Fre: 9-18 | Lör: 9-13',
    footer_rights: 'Alla rättigheter förbehållna.',
    footer_privacy: 'Integritetspolicy',
    footer_terms: 'Villkor',
    footer_tagline: 'Premium Djurfrekvensterapi',
    privacy_last_updated: 'Senast uppdaterad: Mars 2026',
    privacy_section1_title: 'Information Vi Samlar In',
    privacy_section1_text: 'Vi samlar in personlig information som du frivilligt ger oss när du kontaktar oss, inklusive namn, e-post, telefonnummer och detaljer om ditt husdjur.',
    privacy_section2_title: 'Användning av Information',
    privacy_section2_text: 'Vi använder din information för att tillhandahålla våra ANF Animal terapitjänster, boka möten, kommunicera med dig och förbättra våra tjänster.',
    privacy_section3_title: 'Dataskydd',
    privacy_section3_text: 'Vi implementerar lämpliga säkerhetsåtgärder för att skydda din personliga information mot obehörig åtkomst, ändring eller förstörelse.',
    privacy_section4_title: 'Cookies',
    privacy_section4_text: 'Vår webbplats använder cookies för att förbättra din surfupplevelse. Du kan konfigurera din webbläsare för att avvisa cookies.',
    privacy_section5_title: 'Dina Rättigheter',
    privacy_section5_text: 'Enligt GDPR har du rätt att få tillgång till, rätta, radera och överföra dina personuppgifter. Kontakta oss för att utöva dessa rättigheter.',
    privacy_section6_title: 'Kontakt',
    privacy_section6_text: 'För integritetsfrågor, kontakta oss på geral@anfanimalclinicportugal.com, per telefon +351 928 281 833, eller besök vår klinik på R. Porches Velho, 8400-490 Porches.',
    terms_last_updated: 'Senast uppdaterad: Mars 2026',
    terms_section1_title: 'Godkännande av Villkor',
    terms_section1_text: 'Genom att besöka och använda ANFAnimal Portugals webbplats godkänner du dessa villkor. Om du inte samtycker, använd inte webbplatsen.',
    terms_section2_title: 'Tjänster',
    terms_section2_text: 'ANFAnimal Portugal erbjuder icke-invasiv frekvensterapi för djur med ANF-teknologi. Resultaten kan variera och vi garanterar inte specifika resultat.',
    terms_section3_title: 'Immateriella Rättigheter',
    terms_section3_text: 'Allt innehåll på webbplatsen, inklusive texter, bilder, logotyper och design, tillhör ANFAnimal Portugal och skyddas av upphovsrätt.',
    terms_section4_title: 'Ansvarsbegränsning',
    terms_section4_text: 'ANFAnimal Portugal ansvarar inte för indirekta skador till följd av användningen av våra tjänster. ANF-terapi är komplementär och ersätter inte konventionell veterinärbehandling.',
    terms_section5_title: 'Avbokningar och Återbetalningar',
    terms_section5_text: 'Möten kan avbokas 24 timmar i förväg utan kostnad. Sena avbokningar kan medföra en avgift.',
    terms_section6_title: 'Tillämplig Lag',
    terms_section6_text: 'Dessa villkor regleras av portugisisk lag. Eventuella tvister ska hänskjutas till domstolarna i Portimão, Portugal.',
    academy_hero_title: 'Utbildningsakademi',
    academy_hero_subtitle: 'Hitta grundorsaken till varje smärta, skada och/eller patologi och förstå djuranatomin som en enhet. 6 utbildningsnivåer för att bemästra ANF Animal Therapy®.',
    academy_pillars_title: 'Kärnpelare',
    academy_pillar1_title: 'Neurologisk Funktion',
    academy_pillar1_desc: 'Komplexiteten och funktionerna i nervsystemet som gör att djuret kan självläka med hjälp av ANF Animal Therapy®.',
    academy_pillar2_title: 'Biokemi & Resurser',
    academy_pillar2_desc: 'Rollen av celler, molekyler, hormoner och vitaminer och deras interaktioner som behövs för att underlätta fusionerna och frekvenserna som krävs för att reparera dysfunktionerna.',
    academy_pillar3_title: 'Neurofrekvens Medicin',
    academy_pillar3_desc: 'Normalisera inflammation och neurologisk dysfunktion med det mest effektiva teknologiska apparaten med revolutionerande neurofrekvenser som gör att kroppen kan självreparera utan biverkningar eller mediciner.',
    academy_pillar4_title: 'Klinisk Utbildning & Implementering',
    academy_pillar4_desc: 'Lär dig neurologiska undersökningstekniker för enastående palpatoriska och praktiska färdigheter i muskuloskeletala, nerv-, lymf-, matsmältnings- och endokrina system.',
    academy_curriculum: 'Kursplan',
    academy_edu_title: '6 Utbildningsnivåer',
    academy_edu_subtitle: 'En strukturerad väg till att bemästra ANF Animal Therapy®',
    academy_level1_title: 'Smärta & Inflammation',
    academy_level1_topics: 'Frekvenser & Metodik,Smärta & Inflammation,Antioxidanter,Protokoll & Tillämpning',
    academy_level2_title: 'Nervsystemet',
    academy_level2_topics: 'Nervsystemet,Artsjämförelse,Behandlingsplaner,Protokoll & Tillämpning',
    academy_level3_title: 'Lymfsystemet',
    academy_level3_topics: 'Lymfcirkulation,Lymf Biofunktioner inkl. Immunsystem,Lymfundersökning,Lymfbehandling',
    academy_level4_title: 'Hjärt / Behandlingsfaser',
    academy_level4_topics: 'Hjärtsystem,Läkningsfaser,Behandlingsfaser,Protokoll & Tillämpning',
    academy_level5_title: 'Prestation & Återhämtning',
    academy_level5_topics: 'Djurenergi,Organenergi,Cellulär Energi,Testosteron / Hormoner',
    academy_level6_title: 'Praktisk Kurs',
    academy_level6_topics: 'Undersökningstekniker,Protokolldesign,Skivtillämpning,Slutbedömning',
    academy_impl_title: 'Framgångsrik Implementering',
    academy_impl_subtitle: 'Allt du behöver för framgångsrik implementering av ANF Animal Therapy®.',
    academy_impl_tools: 'Protokolltillämpning,Nedladdningar & Mallar,Självstudier & Quiz,Palpationsträning,Mentorskap & Forum,Fall & Resultat,Examen & Certifikat',
    academy_join_title: 'Vem Kan Delta?',
    academy_join_subtitle: 'Välkommen till ANF Animal Therapy® Utbildning.',
    academy_join_roles: 'Veterinärer,Hästfysioterapeuter,Hästosteopater,Hästakupunktörer,Hästmassageterapeuter,Hästkiropraktorer,Skötare,Djurtränare',
    cookie_text: 'Denna webbplats använder cookies för att förbättra din upplevelse. Genom att fortsätta surfa godkänner du vår användning av cookies i enlighet med europeisk lagstiftning.',
    cookie_accept: 'Acceptera',
    cookie_reject: 'Avvisa',
    cookie_privacy_link: 'Integritetspolicy',
  },
};
